import { useCallback, useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Folder, FolderOpen, FileVideo, Tag, PencilSimple, Check, X,
  Plus, MagnifyingGlass, Funnel, Clock, CheckCircle, Warning, SpinnerGap,
  BookOpen, Trash, CaretDown, CaretRight, ArrowClockwise, FilmSlate, PauseCircle, Play, Heart, Prohibit,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "../lib/uiDialogs";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent } from "../utils/permissions";
import KnowledgeExplorerTab from "../components/KnowledgeExplorerTab";
import TagSuggestionsBar from "../components/video/TagSuggestionsBar";
import TagSearchBar from "../components/TagSearchBar";
import DeleteVideoCascadeModal from "../components/DeleteVideoCascadeModal";
// Vincent B930 (Eric 2026-02-14 SPEC STRICK PROD) — Zone bulk import
// des documents textuels, visible en haut de l'onglet Textes.
import TextDocsBulkImportZone from "../components/text/TextDocsBulkImportZone";
// Tâche 1438 — Journal des fichiers refusés, pour voir les vraies causes.
import ImportRefusalsPanel from "../components/text/ImportRefusalsPanel";
// Tâche 1435 — Source unique des formats acceptés (défaut 1421 : une copie
// par écran vieillit dès le format suivant).
import { LISTE_DOC } from "../lib/formatsImport";
// Vincent B931 (Eric 2026-02-14 SPEC STRICK PROD) — Modal Batch Retag
// (choix d'un ou plusieurs tags canoniques + application en masse).
import BatchTagsModal from "../components/video/BatchTagsModal";
// Vincent (2026-02) ticket #520 — Labels EXACTS des étapes du pipeline pour
// affichage dans le badge "EN COURS".
import { PIPELINE_STAGES, pipelineStageLabel } from "../lib/videoDetailFormat";
// Vincent (2026-02) — Refonte : persistance édition vidéo centralisée
// (Catégorie + diff Parcours). Partagée avec VideoDetailPage/VideoDetailHeader.
import {
  saveCategoryIfChanged,
  savePathwayDiff,
  pathwayIdsForVideo,
} from "../lib/videoEditPersistence";

import { API_BASE as API } from "../lib/apiBase";
import { libelleSource } from "../lib/sourceTranscription";
// Eric 2026-07-30 (B609 enhancement, 2e itération) — Highlight partagé.
import { HighlightedText } from "../lib/highlight";
// Vincent B629 (Eric 2026-07-30 SPEC STRICK PROD) — Source FR-only des thèmes.
import { TRAINING_THEMES, trainingThemeLabel } from "../lib/trainingThemes";
import { matchesPathwayQuery } from "../lib/pathwayFilter";
import { foldAccents } from "../lib/textFold"; // Tâche 1170
import { matchesId, extractIdQuery } from "../lib/videoIdSearch"; // Tâche 1304
import { loadRecentIdSearches, rememberIdSearch, forgetIdSearch } from "../lib/recentIdSearches"; // Tâche 1305
import RecentIdSearches from "../components/RecentIdSearches"; // Tâche 1305
import { useCollapsedPathGroups } from "../lib/useCollapsedPathGroups"; // Tâche 1166
import { useRecentPathways } from "../lib/useRecentPathways"; // Tâche 1168
import BulkGapInjectButtons from "../components/gaps/BulkGapInjectButtons"; // Tâche 1278
import EtaAttente from "../components/video/EtaAttente"; // Tâche 1476
import MenuContinuerPipeline from "../components/video/MenuContinuerPipeline"; // Tâche 1493





export default function FileExplorerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tr, lang } = useLang();
  const [activeTab, setActiveTab] = useState("videos"); // Vincent #137 — 'videos' | 'knowledge'
  const [videos, setVideos] = useState([]);
  const [modules, setModules] = useState([]);
  const [allTags, setAllTags] = useState([]);
  // Vincent B835 (Eric 2026-02-14 SPEC STRICK PROD) — Renommage inline
  // d'un dossier de l'explorateur (cascade sur toutes les vidéos incluses).
  const [renamingFolder, setRenamingFolder] = useState(null); // fullPath du dossier en cours de renommage
  const [renameDraft, setRenameDraft] = useState("");
  const [renamingBusy, setRenamingBusy] = useState(false);
  // Tâche 1435 (Eric 2026-10-02) — Un classeur Excel donne un document par
  // feuille. Eric a demandé un REGROUPEMENT PLIABLE par classeur : à plat,
  // dix feuilles d'un même fichier noient le reste de la liste.
  // Replié PAR DÉFAUT : c'est le classeur qui est l'objet importé, la feuille
  // n'est qu'un de ses morceaux.
  const [classeursOuverts, setClasseursOuverts] = useState(new Set());
  const basculerClasseur = (gid) => setClasseursOuverts((prev) => {
    const suivant = new Set(prev);
    if (suivant.has(gid)) suivant.delete(gid); else suivant.add(gid);
    return suivant;
  });
  const startFolderRename = (node) => {
    setRenamingFolder(node.fullPath);
    setRenameDraft(node.name || "");
  };
  const cancelFolderRename = () => {
    setRenamingFolder(null);
    setRenameDraft("");
  };
  const commitFolderRename = async (node) => {
    const trimmed = (renameDraft || "").trim();
    if (!trimmed || trimmed === node.name) { cancelFolderRename(); return; }
    if (trimmed.includes(" / ")) {
      toast.error(tr("Le nom ne peut pas contenir « / »", "The name cannot contain “/”"));
      return;
    }
    setRenamingBusy(true);
    try {
      const resp = await axios.post(
        `${API}/api/videos/folder/rename`,
        { old_path: node.fullPath, new_name: trimmed },
        { withCredentials: true },
      );
      const affected = resp?.data?.affected_total || 0;
      toast.success(`Dossier renommé (${affected} vidéo${affected !== 1 ? "s" : ""} mise${affected !== 1 ? "s" : ""} à jour)`);
      cancelFolderRename();
      // Recharge la liste + les modules pour refléter le nouveau chemin.
      await loadAll();
    } catch (e) {
      console.error("[FileExplorer.rename-folder]", e);
      const detail = e?.response?.data?.detail || "Échec du renommage";
      toast.error(detail, { duration: 8000 });
    } finally {
      setRenamingBusy(false);
    }
  };
  // Vincent 2026-05-05 — catégories assignables depuis l'explorateur
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  // Tâche 1484 (Eric) — Les fautes tapées dans ce filtre alimentent le
  // tableau des fautes fréquentes de l'écran Orthographe. Envoi différé de
  // 900 ms : sans l'attente, chaque lettre d'un mot en cours de frappe
  // partirait comme une faute (« pre », « pret », « preti »…).
  useEffect(() => {
    const saisie = (search || "").trim();
    if (saisie.length < 4) return undefined;
    const minuteur = setTimeout(() => {
      axios.post(
        `${API}/api/orthographe/recherche-saisie`,
        { saisie, source: "explorateur" },
        { withCredentials: true },
      ).catch(() => {});
    }, 900);
    return () => clearTimeout(minuteur);
  }, [search]);
  const [filterModule, setFilterModule] = useState("all");
  const [filterTag, setFilterTag] = useState("all");
  // Vincent 2026-05-04: tri par critère (date / nom / durée)
  // Vincent #295 (2026-06-12) — Tri A→Z par défaut.
  // Vincent B718 (Eric 2026-02-11) — Persistance du tri dans sessionStorage
  // pour que les boutons Précédent/Suivant sur la page vidéo respectent
  // le MÊME ordre que l'explorateur.
  const [sortBy, setSortBy] = useState(() => {
    try {
      return sessionStorage.getItem("fileExplorer.sortBy") || "name_asc";
    } catch (_) { return "name_asc"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("fileExplorer.sortBy", sortBy); } catch (_) { /* silencieux */ }
  }, [sortBy]);
  // Vincent B894 (Eric 2026-02-14 SPEC STRICK PROD) — Map video_id →
  // {coverage_ratio, coverage_alert, gap_min} pour le tri « Vidéos à
  // couverture faible » et l'affichage inline du badge %.
  const [coverageMap, setCoverageMap] = useState({});
  // Filtre chip actif « ⚠️ Couverture faible » : true → n'affiche que les
  // vidéos ayant `content_coverage_alert === true` (ratio < 0.9 selon B885).
  const [lowCoverageOnly, setLowCoverageOnly] = useState(false);
  // Tâche 1341 (Eric 2026-06-30) — Ne voir que les vidéos dont la synthèse
  // n'est pas encore vérifiée sur le web. Sans ce filtre, repérer le travail
  // qui reste imposait de parcourir 200 lignes à l'œil.
  const [notVerifiedOnly, setNotVerifiedOnly] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/videos/coverage-report`, { withCredentials: true });
        if (cancelled) return;
        const map = {};
        for (const it of (data?.items || [])) {
          if (it.video_id) map[it.video_id] = it;
        }
        setCoverageMap(map);
      } catch { /* silent — feature non bloquante */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Vincent B704 résiduel (Eric 2026-02-10) — `expandedModules` (objet 3-états
  // avec défaut ouvert via `!== false`) remplacé par `collapsedPaths` (Set 2-états
  // strict) plus bas dans le fichier. Voir bloc `toggleModule` (~ligne 445).
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  // Tâches 1157 et 1166 — Familles de parcours repliées, mémorisées d'un
  // fichier au suivant (hook partagé avec la fiche du fichier).
  const { collapsed: collapsedPathwayTypes, toggleGroup: togglePathwayType } =
    useCollapsedPathGroups();
  // Tâche 1168 — Derniers parcours cochés, en tête du menu.
  const { recentIds, remember: rememberPathway } = useRecentPathways();
  // Tâche 1164 (Eric 2026-06-17) — Recherche dans le menu des parcours.
  const [pathwayQuery, setPathwayQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [assigningCatVid, setAssigningCatVid] = useState(null); // video_id currently being categorized
  // Vincent #163 (Eric 2026-05-24) — Sélection multiple + bouton "Assigner à un parcours"
  const [selectedVideoIds, setSelectedVideoIds] = useState([]);
  const [bulkStopBusy, setBulkStopBusy] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  // Vincent B931 (Eric 2026-02-14 SPEC STRICK PROD) — Modal Batch Retag
  // (choix d'un ou plusieurs tags canoniques + application en masse).
  const [showBatchTagsModal, setShowBatchTagsModal] = useState(false);
  // Vincent #163 — Dropdown checklist par vidéo : précharge tous les parcours de l'org
  const [allPathways, setAllPathways] = useState([]);
  const [openPathwayDropdown, setOpenPathwayDropdown] = useState(null); // video.id
  // Vincent #287 (2026-06-11) — position fixe du menu Parcours (anti-clipping)
  const [pathwayDropdownPos, setPathwayDropdownPos] = useState({ top: 0, right: 0 });
  // Vincent #394 (Eric 2026-07-02 SPEC STRICK) — Progression de visionnage
  // par vidéo pour l'utilisateur courant : { video_id: {watch_pct, seconds_watched, watched_ok} }
  const [watchProgress, setWatchProgress] = useState({});
  // Bug 545 (Eric 2026-07-24) — Set des IDs favorites du user courant, pour
  // afficher l'étoile pleine/vide sur chaque row. On stocke uniquement les
  // item_id (video/document) — le type est déjà connu (les KUs favorites
  // s'affichent ailleurs).
  // Vincent B604 (Eric 2026-07-29 SPEC STRICK) — Les documents texte (.txt,
  // .md, .docx, .pdf) sont stockés dans la collection `videos` (avec
  // `is_text_doc=True`), donc les deux onglets partagent la même Set et le
  // même item_type="video". Le ♥ affiché doit rester rempli tant que
  // l'utilisateur ne l'a pas retiré (POST/DELETE optimistes ci-dessous).
  const [favVideoIds, setFavVideoIds] = useState(new Set());
  // Vincent (2026-02) #527 — États pour boutons Relancer + Ré-optimiser inline.
  const [reprocessingVid, setReprocessingVid] = useState(null);
  const [reoptimizingVid, setReoptimizingVid] = useState(null);
  // Vincent B979 (Eric 2026-02-26 SPEC STRICK PROD) — Bulk re-merge fusion
  // sur la sélection courante (typiquement combiné avec le filtre
  // « ⚠️ Couverture faible » pour rejouer les vidéos qui ont perdu du
  // contenu à la fusion). Appelle POST /admin/videos/bulk-rerun-fusion
  // avec `video_ids: selectedVideoIds`.
  const [bulkRemergingBusy, setBulkRemergingBusy] = useState(false);
  // Vincent B981 (Eric 2026-02-26 SPEC STRICK PROD) — Progress tracker
  // poll toutes les 2s pendant qu'un bulk rerun est en cours.
  const [bulkRerunProgress, setBulkRerunProgress] = useState(null);
  // Tâche 1261 — Menu de la copie fidèle en lot (Fireflies seul / Whisper seul).
  const [bulkCopyMenuOpen, setBulkCopyMenuOpen] = useState(false);

  const runBulkCopy = async (source) => {
    setBulkCopyMenuOpen(false);
    if (bulkRemergingBusy) return;
    const label = source === "fireflies" ? "Fireflies" : "Whisper";
    if (selectedVideoIds.length === 0) {
      toast.warning(tr(
        "⚠️ Aucune vidéo sélectionnée. Cochez d'abord les vidéos à copier.",
        "⚠️ No video selected. Tick the videos to copy first.",
      ));
      return;
    }
    const ok = await confirmDialog(
      tr(
        `Copier la transcription ${label} telle quelle dans la version fusionnée de ${selectedVideoIds.length} vidéo(s) ? ` +
        `Aucune comparaison, aucun appel IA : texte, horodatages et locuteurs sont conservés à l'identique, ` +
        `puis la casse et les accents du dictionnaire sont rétablis. Un instantané est pris avant chaque copie (retour arrière possible). ` +
        `Les vidéos sans transcription ${label}, et celles dont la source n'a pas changé depuis la dernière fusion, sont ignorées.`,
        `Copy the ${label} transcript as-is into the merged version of ${selectedVideoIds.length} video(s)? ` +
        `No comparison, no AI call: text, timestamps and speakers are kept identical, ` +
        `then the dictionary casing and accents are restored. A snapshot is taken before each copy (undo possible). ` +
        `Videos without a ${label} transcript, and those whose source has not changed since the last merge, are skipped.`,
      ),
      { tone: "warning", confirmLabel: tr(`Copier ${label}`, `Copy ${label}`) },
    );
    if (!ok) return;
    setBulkRemergingBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/videos/bulk-rerun-fusion`,
        { video_ids: selectedVideoIds, sources: [source], skip_unchanged: true },
        { withCredentials: true, timeout: 180000 },
      );
      const launched = data?.total_launched ?? 0;
      const unchanged = data?.total_unchanged ?? 0;
      const skipped = (data?.total_skipped ?? 0) - unchanged;
      if (launched === 0) {
        toast.warning(
          `Aucune copie lancée — ${unchanged} déjà à jour, ${skipped} sans transcription ${label}.`,
        );
      } else {
        toast.success(
          `✅ ${launched} copie(s) ${label} lancée(s)` +
          (unchanged > 0 ? `, ${unchanged} déjà à jour` : "") +
          (skipped > 0 ? `, ${skipped} sans ${label}` : "") + ".",
        );
      }
    } catch (err) {
      toast.error(tr("Erreur : ", "Error: ") + (err?.response?.data?.detail || err.message));
    } finally {
      setBulkRemergingBusy(false);
    }
  };

  // Vincent B981 (Eric 2026-02-26) — Poll GET /bulk-rerun-fusion/progress
  // toutes les 2s pendant qu'un bulk rerun est en cours. Stop dès que
  // le status devient "done" ou "idle", ou après 30 min max (safety).
  useEffect(() => {
    if (!bulkRemergingBusy && !bulkRerunProgress) return;
    let cancelled = false;
    let elapsed = 0;
    const maxElapsedMs = 30 * 60 * 1000;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(
          `${API}/api/admin/videos/bulk-rerun-fusion/progress`,
          { withCredentials: true },
        );
        if (cancelled) return;
        setBulkRerunProgress(data);
        if (data?.status === "done" || data?.status === "idle" || elapsed > maxElapsedMs) {
          setTimeout(() => {
            if (!cancelled) setBulkRerunProgress(null);
          }, 30000);
          return;
        }
      } catch (_err) { /* silencieux */ }
      elapsed += 2000;
      if (!cancelled) setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkRemergingBusy]);

  // Vincent B585 récurrence (Eric B695 2026-02-XX) — Boutons « Continuer
  // (connaissances) » et « Recommencer (transcription) » directement dans
  // l'Explorateur de fichiers, à côté des badges de pause. Vincent voulait
  // ces boutons visibles SANS avoir à ouvrir la fiche vidéo (déjà présents
  // dans VideoPipelineStatus.js sur la page de détail).
  const [resumeBusyVid, setResumeBusyVid] = useState(null);
  // Tâche 1172 (Eric 2026-06-25) — Les tags ne s'étalent plus sous le nom du
  // fichier : un bouton « Tags » ouvre la liste dans une fenêtre contextuelle.
  const [openTagsVid, setOpenTagsVid] = useState(null);

  // Tâche 1173 (Eric 2026-06-25) — Les gestionnaires « Recommencer
  // (transcription) » et « Continuer (connaissances) » ont été retirés de
  // l'Explorateur avec leurs boutons : ils encombraient la ligne du fichier.
  // Ils restent disponibles sur la fiche du fichier (VideoPipelineStatus.js).

  // B1116 (Eric 2026-09-08) — Arrêt forcé depuis l'Explorateur : c'est ici
  // qu'Eric suit plusieurs vidéos à la fois, donc c'est ici qu'il repère un
  // pipeline qui boucle sur un bogue et consomme des crédits pour rien.
  // Différent de la pause : état terminal, « Recommencer » repart de zéro.
  const doForceStopInList = async (videoId) => {
    const ok = await confirmDialog(
      tr(
        "Forcer l'arrêt du pipeline ?\n\n" +
          "• L'étape en cours se termine (impossible de l'interrompre en plein vol)\n" +
          "• Aucune étape suivante ne démarrera — plus aucun crédit consommé\n" +
          "• La vidéo passe en « arrêté » : « Recommencer (transcription) » repart de zéro\n\n" +
          "À utiliser quand le pipeline boucle sur un bogue.",
        "Force-stop the pipeline?\n\n" +
          "• The current step finishes (it cannot be interrupted mid-flight)\n" +
          "• No further step will start — no more credits consumed\n" +
          "• The video becomes “stopped”: “Restart (transcription)” starts over\n\n" +
          "Use this when the pipeline loops on a bug.",
      )
    );
    if (!ok) return;
    setResumeBusyVid(videoId);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/queue/cancel`,
        {},
        { withCredentials: true },
      );
      const saved = savingsLine(data?.saved_estimate);
      toast.success(
        (data?.note || "Arrêt du pipeline demandé") + (saved ? ` ${saved}.` : ""),
        { duration: 8000 },
      );
      await loadAll();
    } catch (e) {
      toast.error(explainError(e, tr("Impossible d'arrêter le pipeline", "Could not stop the pipeline")));
    } finally {
      setResumeBusyVid(null);
    }
  };

  // B1117 (Eric 2026-09-08) — Ce qu'un arrêt a évité de consommer. Les
  // étapes et appels évités sont des chiffres solides ; le montant est une
  // estimation aux tarifs publics, et le message le dit.
  const savingsLine = (saved) => {
    if (!saved || !saved.model_calls_skipped) return null;
    return `${saved.stages_skipped_count} étape(s) et ${saved.model_calls_skipped} appel(s) de modèle évités`
      + ` — environ ${saved.usd_estimate} $ US (estimation, tarifs publics)`;
  };

  const doForceStopBulk = async () => {
    if (selectedVideoIds.length === 0) {
      toast.warning(tr(
        "⚠️ Aucun élément sélectionné. Cochez d'abord les vidéos dont vous voulez arrêter le pipeline.",
        "⚠️ Nothing selected. Tick the videos whose pipeline you want to stop first.",
      ));
      return;
    }
    const ok = await confirmDialog(
      tr(
        `Forcer l'arrêt du pipeline sur ${selectedVideoIds.length} élément(s) ?\n\n` +
          "• L'étape en cours de chaque vidéo se termine (impossible de l'interrompre en plein vol)\n" +
          "• Aucune étape suivante ne démarrera — plus aucun crédit consommé\n" +
          "• Les vidéos déjà terminées sont ignorées\n\n" +
          "À utiliser quand une panne bloque plusieurs vidéos ensemble.",
        `Force-stop the pipeline on ${selectedVideoIds.length} item(s)?\n\n` +
          "• The current step of each video finishes (it cannot be interrupted mid-flight)\n" +
          "• No further step will start — no more credits consumed\n" +
          "• Videos already completed are skipped\n\n" +
          "Use this when one outage blocks several videos at once.",
      )
    );
    if (!ok) return;
    setBulkStopBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/queue/cancel-bulk`,
        { video_ids: selectedVideoIds },
        { withCredentials: true },
      );
      const saved = savingsLine(data?.saved_estimate);
      toast.success(
        `${data.cancelled_count} pipeline(s) arrêté(s)`
        + (data.skipped_count ? `, ${data.skipped_count} ignoré(s) (déjà terminés)` : "")
        + (saved ? `. ${saved}` : ""),
        { duration: 8000 },
      );
      await loadAll();
    } catch (e) {
      toast.error(explainError(e, tr("Impossible d'arrêter les pipelines sélectionnés", "Could not stop the selected pipelines")));
    } finally {
      setBulkStopBusy(false);
    }
  };

  const reprocessVideo = async (videoId) => {
    setReprocessingVid(videoId);
    try {
      await axios.post(`${API}/api/videos/${videoId}/reprocess`, {}, { withCredentials: true });
      toast.success(tr(
        "Traitement relancé — état 'en cours' dans quelques instants.",
        "Processing restarted — the status turns to “in progress” shortly.",
      ));
      await loadAll();
    } catch (e) {
      console.error("[reprocessVideo]", e);
      toast.error(explainError(e, tr("Échec du relancement", "Restart failed")));
    } finally {
      setReprocessingVid(null);
    }
  };

  const reoptimizeVideo = async (videoId) => {
    setReoptimizingVid(videoId);
    try {
      await axios.post(`${API}/api/videos/${videoId}/optimize`, {}, { withCredentials: true });
      toast.success(tr("Ré-optimisation FFmpeg lancée en arrière-plan.", "FFmpeg re-optimization started in the background."));
      await loadAll();
    } catch (e) {
      console.error("[reoptimizeVideo]", e);
      toast.error(explainError(e, tr("Échec de la ré-optimisation", "Re-optimization failed")));
    } finally {
      setReoptimizingVid(null);
    }
  };

  const togglePathwayMembership = async (videoId, pathway) => {
    // Soit on retire la vidéo du parcours, soit on l'ajoute
    const isMember = (pathway.video_ids || []).includes(videoId);
    const newIds = isMember
      ? pathway.video_ids.filter(id => id !== videoId)
      : [...(pathway.video_ids || []), videoId];
    try {
      await axios.patch(
        `${API}/api/pathways/${pathway.id}`,
        { video_ids: newIds },
        { withCredentials: true }
      );
      setAllPathways(prev => prev.map(p => p.id === pathway.id ? { ...p, video_ids: newIds } : p));
      toast.success(isMember
        ? `Retirée de "${pathway.name}"`
        : `Ajoutée à "${pathway.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Échec", "Failed"));
    }
  };

  const toggleSelectVideo = (id) => {
    setSelectedVideoIds((arr) => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
  };
  const clearSelection = () => setSelectedVideoIds([]);

  // Vincent #275/#291 (2026-06-11) — Modale avec liste à cocher des éléments à
  // conserver (remplace l'ancien enchaînement window.confirm/window.prompt).
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Eric 2026-06-25 (iter117 B2) — migré vers canEditContent (helper canonique,
  // aligné backend require_editor). Whitelist élargie : inclut désormais aussi
  // ceo/president/vp/concepteur/employee_advanced/access_level=avance.
  const isAdmin = canEditContent(user);

  // Tâche 1305 (Eric 2026-06-30) — Les derniers identifiants cherchés. On
  // ne retient un identifiant QUE s'il a réellement trouvé un fichier :
  // une frappe partielle ou une faute n'a rien à faire dans la liste.
  const [recentIdSearches, setRecentIdSearches] = useState(() => loadRecentIdSearches());
  useEffect(() => {
    if (!isAdmin) return undefined;
    const fragment = extractIdQuery(search);
    if (!fragment) return undefined;
    const hit = videos.find((v) => String(v.id || "").toLowerCase().includes(fragment));
    if (!hit) return undefined;
    const timer = setTimeout(
      () => setRecentIdSearches(rememberIdSearch({ id: hit.id, title: hit.title })),
      800,
    );
    return () => clearTimeout(timer);
  }, [search, videos, isAdmin]);

  // Tâche 1491 (Eric) — « Arrêter le pipeline (terminé) », l'équivalent du
  // « Sortir de la file » de la File d'attente, mais depuis l'Explorateur.
  // Le serveur dit d'abord ce qu'il conclurait (lecture seule) : si aucune
  // connaissance autre que la synthèse n'existe, on NOMME EN ROUGE les
  // étapes non faites et on demande une confirmation explicite — marquer
  // « terminé » une vidéo dont rien n'a été extrait ferait disparaître du
  // travail réel.
  const arreterPipelineTermine = async (video) => {
    try {
      const { data: apercu } = await axios.get(
        `${API}/api/videos/${video.id}/pipeline/conclusion-apercu`,
        { withCredentials: true },
      );
      const manquants = apercu?.manquants || [];
      const corps = apercu?.complet ? (
        <span>
          {tr(
            `Des connaissances ont déjà été générées pour « ${video.title} ». Le pipeline sera affiché comme TERMINÉ et la vidéo quittera la file d'attente. Ses données restent intactes.`,
            `Knowledge has already been generated for “${video.title}”. The pipeline will be shown as COMPLETE and the video will leave the queue. Its data stays untouched.`,
          )}
          {manquants.length > 0 && (
            <span className="block mt-2">
              {tr("Jamais produit : ", "Never produced: ")}
              <strong className="text-rose-600">{manquants.join(", ")}</strong>
            </span>
          )}
        </span>
      ) : (
        <span>
          {tr(
            `Aucune connaissance autre que la synthèse n'a été générée pour « ${video.title} ».`,
            `No knowledge other than the synthesis was generated for “${video.title}”.`,
          )}
          <span className="block mt-2">
            {tr("Étapes non faites : ", "Steps not done: ")}
            <strong className="text-rose-600">{manquants.join(", ") || tr("toutes", "all")}</strong>
          </span>
          <span className="block mt-2">
            {tr(
              "Le pipeline sera affiché comme PARTIELLEMENT COMPLÉTÉ. Confirmer ?",
              "The pipeline will be shown as PARTIALLY COMPLETE. Confirm?",
            )}
          </span>
        </span>
      );
      const ok = await confirmDialog(corps, {
        tone: apercu?.complet ? "info" : "warning",
        confirmLabel: tr("Arrêter le pipeline", "Stop the pipeline"),
      });
      if (!ok) return;
      const { data } = await axios.post(
        `${API}/api/videos/${video.id}/queue/detach`, {},
        { withCredentials: true },
      );
      toast.success(data?.pipeline_complet
        ? tr("Pipeline affiché comme TERMINÉ.", "Pipeline shown as COMPLETE.")
        : tr(
          `Pipeline affiché comme PARTIELLEMENT COMPLÉTÉ — il manque : ${(data?.manquants || []).join(", ") || "tout"}.`,
          `Pipeline shown as PARTIALLY COMPLETE — missing: ${(data?.manquants || []).join(", ") || "everything"}.`,
        ), { duration: 8000 });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail
        || tr("Arrêt impossible", "Could not stop"));
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // Vincent #303 (2026-06-12) — Selon l'onglet actif, on charge les vidéos
      // OU les fichiers textuels (mêmes filtres/tri/édition côté UI). Lorsqu'on
      // est sur l'onglet "knowledge", on charge également les vidéos pour
      // alimenter le composant KnowledgeExplorerTab.
      const kind = activeTab === "texts" ? "text" : "video";
      const [vRes, mRes, cRes] = await Promise.all([
        axios.get(`${API}/api/videos?limit=200&kind=${kind}`, { withCredentials: true }),
        axios.get(`${API}/api/videos/modules/list`, { withCredentials: true }),
        axios.get(`${API}/api/content-categories`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setVideos(vRes.data.items || []);
      setModules(mRes.data.modules || []);
      setAllTags(mRes.data.tags || []);
      setCategories(Array.isArray(cRes.data) ? cRes.data : (cRes.data.categories || []));
      // Vincent #163 — précharge les parcours pour le dropdown par vidéo
      try {
        const pRes = await axios.get(`${API}/api/pathways`, { withCredentials: true });
        setAllPathways(pRes.data.pathways || []);
      } catch (_) { /* ignore */ }
      // Vincent #394 (Eric 2026-07-02 SPEC STRICK) — Progression de visionnage
      // par vidéo pour afficher le % sous le nom du fichier.
      try {
        const wpRes = await axios.get(`${API}/api/videos/my-watch-progress`, { withCredentials: true });
        setWatchProgress(wpRes.data || {});
      } catch (_) { /* non-bloquant */ }
      // Bug 545 (Eric 2026-07-24) — Charge les favoris du user pour afficher
      // les étoiles pleines sur les rows favorites. Silencieux si l'endpoint
      // échoue (l'étoile restera vide, pas de blocage UX).
      try {
        const favRes = await axios.get(`${API}/api/favorites`, { withCredentials: true });
        const list = Array.isArray(favRes.data) ? favRes.data : [];
        setFavVideoIds(new Set(list.filter(f => f.item_type === "video").map(f => f.item_id)));
      } catch (_) { /* non-bloquant */ }
    } catch (e) {
      console.error("[loadAll]", e);
      toast.error(explainError(e, tr("Échec du chargement de l'explorateur", "Could not load the explorer")), { duration: 8000 });
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Bug 545 (Eric 2026-07-24) — Bascule le favori d'une vidéo/document.
  // Optimiste : met à jour la Set locale IMMÉDIATEMENT puis rollback en cas
  // d'erreur backend (pattern courant pour éviter le flicker).
  // Vincent B604 (Eric 2026-07-29 SPEC STRICK) — Les documents texte sont
  // stockés dans la même collection `videos` (avec is_text_doc=True), donc
  // on utilise TOUJOURS item_type="video" pour l'API favoris, y compris
  // dans l'onglet Textes. Le rendu ♥ est piloté par favVideoIds.
  const toggleFavorite = async (v) => {
    const wasFav = favVideoIds.has(v.id);
    // Optimistic update
    setFavVideoIds((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(v.id);
      else next.add(v.id);
      return next;
    });
    try {
      if (wasFav) {
        await axios.delete(`${API}/api/favorites/video/${v.id}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/favorites`, { type: "video", item_id: v.id }, { withCredentials: true });
      }
      // Vincent B605 (2026-07-29) — Notifie les autres pages (Dashboard,
      // Base de connaissances, Dictionnaire) que la liste des favoris a
      // changé, pour qu'elles rafraîchissent leur ♥ persistant.
      try { window.dispatchEvent(new CustomEvent("favorites:updated")); } catch (_) { /* silencieux */ }
    } catch (e) {
      // Rollback + toast
      setFavVideoIds((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(v.id);
        else next.delete(v.id);
        return next;
      });
      toast.error(explainError(e, tr("Impossible de mettre à jour les favoris", "Could not update the favourites")));
    }
  };

  // Vincent (2026-02) suivi ticket #520 — Polling léger 30s sur les vidéos
  // en cours de pipeline. Rafraîchit UNIQUEMENT /api/videos (pas modules/
  // catégories/parcours) pour que le badge d'étape (« Transcription
  // (Whisper) », « Diarisation (AssemblyAI) », …) évolue automatiquement
  // sans que l'utilisateur ait besoin de recharger la page. Le polling
  // s'auto-arrête dès qu'il n'y a plus de vidéo `pipeline_status` autre
  // que `complete`/`failed`.
  useEffect(() => {
    const hasInProgress = (videos || []).some(
      (v) => v.pipeline_status !== "complete" && v.pipeline_status !== "failed"
    );
    if (!hasInProgress) return undefined;
    const kind = activeTab === "texts" ? "text" : "video";
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/api/videos?limit=200&kind=${kind}`, { withCredentials: true });
        setVideos(r.data.items || []);
      } catch (e) {
        // Polling silencieux : on ne toast pas en cas d'erreur pour ne pas
        // spammer si le réseau vacille. La prochaine tick réessaiera.
        console.warn("[FileExplorer polling] refresh failed", e?.message);
      }
    };
    const iv = setInterval(tick, 30_000);
    return () => clearInterval(iv);
  }, [videos, activeTab]);

  // Group videos by module
  const grouped = videos.reduce((acc, v) => {
    const mod = v.module_name || "Sans module";
    if (!acc[mod]) acc[mod] = [];
    acc[mod].push(v);
    return acc;
  }, {});

  // Filter + sort
  const sortVideos = (vids) => {
    const arr = [...vids];
    if (sortBy === "date_desc") arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sortBy === "date_asc") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sortBy === "name_asc") arr.sort((a, b) => (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" }));
    else if (sortBy === "name_desc") arr.sort((a, b) => (b.title || "").localeCompare(a.title || "", "fr", { sensitivity: "base" }));
    // Vincent #65 batch FLHQ (Eric 2026-02-06) — tri par catégorie de contenu.
    // Les vidéos sans catégorie sont triées en fin de liste.
    else if (sortBy === "category_asc" || sortBy === "category_desc") {
      const catName = (vid) => {
        const c = (categories || []).find((c) => c.id === vid.category_id);
        return (c?.name || "\uffff").toLowerCase(); // \uffff = trie tout en fin
      };
      arr.sort((a, b) => {
        const cmp = catName(a).localeCompare(catName(b), "fr", { sensitivity: "base" });
        if (cmp !== 0) return sortBy === "category_asc" ? cmp : -cmp;
        // Tie-break : par titre alpha
        return (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" });
      });
    }
    // Vincent B894 (Eric 2026-02-14 SPEC STRICK PROD) — Tri par ratio de
    // couverture ascendant : les vidéos avec le PIRE ratio d'abord (les
    // plus urgentes à corriger). Les vidéos sans diagnostic disponible
    // sont poussées en fin de liste (ratio implicite = 2 > max).
    else if (sortBy === "coverage_asc") {
      const r = (vid) => {
        const c = coverageMap[vid.id];
        return c && c.coverage_ratio != null ? c.coverage_ratio : 2;
      };
      arr.sort((a, b) => {
        const cmp = r(a) - r(b);
        if (cmp !== 0) return cmp;
        return (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" });
      });
    }
    return arr;
  };

  const visibleGrouped = Object.entries(grouped).reduce((acc, [mod, vids]) => {
    if (filterModule !== "all" && filterModule !== mod) return acc;
    const filtered = vids.filter((v) => {
      // Eric 2026-07-30 (B609 enhancement, 2e itération) — Le filtre matche
      // désormais AUSSI le contenu du document texte (`content_preview`,
      // 3 premières lignes calculées côté backend). Empêche le learner de
      // devoir ouvrir chaque doc pour vérifier son contenu.
      // Tâche 1170 (Eric 2026-06-17) — Accents ignorés pour CHERCHER, jamais
      // à l'affichage : « economie » doit trouver « Économie ».
      // Tâche 1304 (Eric 2026-06-30) — Recherche par IDENTIFIANT, en plus du
      // nom, pour les personnes autorisées à concevoir ou corriger. Le
      // fragment suffit, et une URL collée est acceptée telle quelle.
      const q = foldAccents(search);
      const matchSearch = !search
        || (isAdmin && matchesId(v.id, search))
        || foldAccents(v.title).includes(q)
        || (v.tags || []).some((t) => foldAccents(t).includes(q))
        || foldAccents(v.content_preview).includes(q);
      const matchTag = filterTag === "all" || (v.tags || []).includes(filterTag);
      // Vincent B894 (Eric 2026-02-14 SPEC STRICK PROD) — Filtre chip
      // « ⚠️ Couverture faible » : ne garde que les vidéos ayant
      // `content_coverage_alert === true` selon le rapport backend.
      const matchLowCov = !lowCoverageOnly || !!(coverageMap[v.id]?.coverage_alert);
      // Tâche 1341 — « Non vérifiée » = la synthèse n'a pas été vérifiée sur
      // le web (la pastille verte « Vérifiée » manque).
      const matchNotVerified = !notVerifiedOnly || !v.web_verified;
      return matchSearch && matchTag && matchLowCov && matchNotVerified;
    });
    if (filtered.length > 0) acc[mod] = sortVideos(filtered);
    return acc;
  }, {});

  const startEdit = (video) => {
    setEditingId(video.id);
    setEditForm({
      title: video.title || "",
      module_name: video.module_name || "",
      tags: (video.tags || []).join(", "),
      // Vincent (2026-02) — État initial catégorie + parcours (via helper
      // partagé lib/videoEditPersistence.js pour cohérence FE cross-page).
      category_id: video.category_id || "",
      pathway_ids: pathwayIdsForVideo(allPathways, video.id),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Vincent's complaint: "Erreur" alone doesn't help. Extract the most useful detail from any axios error.
  const explainError = (e, fallback = tr("Erreur inconnue", "Unknown error")) => {
    if (!e) return fallback;
    const status = e.response?.status;
    const detail = e.response?.data?.detail;
    const data = e.response?.data;
    if (typeof detail === "string" && detail.trim()) return `${detail} (HTTP ${status || "?"})`;
    if (Array.isArray(detail)) {
      // FastAPI 422 validation error
      const msgs = detail.map((d) => `${d.loc?.join('.') || '?'} : ${d.msg}`).join(" · ");
      return `Validation : ${msgs} (HTTP 422)`;
    }
    if (data && typeof data === "object" && data.message) return `${data.message} (HTTP ${status || "?"})`;
    if (e.code === "ECONNABORTED") return "Délai dépassé — l'opération a pris trop de temps";
    if (e.message?.includes("Network Error")) return "Erreur réseau — vérifie ta connexion";
    if (status) return `Erreur HTTP ${status} — ${e.response?.statusText || "réponse inattendue du serveur"}`;
    return e.message || fallback;
  };

  const saveEdit = async (videoId) => {
    try {
      // Vincent XLSX 2026-07-13 21h Row 46216 (Eric 2026-07-14 SPEC STRICK) —
      // Dédoublonnage des tags case-insensitive avant sauvegarde. Vincent :
      // "s'il y a deux fois le même Tag écrit dans la case, tu ne dois quand
      // même l'afficher qu'une seule fois". On préserve la 1re occurrence
      // (casse originale) et on retire les doublons canonicalisés.
      const rawTags = editForm.tags.split(",").map((t) => t.trim()).filter(Boolean);
      const seen = new Set();
      const tagsArr = [];
      for (const t of rawTags) {
        const canon = t.toLowerCase();
        if (!seen.has(canon)) {
          seen.add(canon);
          tagsArr.push(t);
        }
      }
      await axios.patch(
        `${API}/api/videos/${videoId}/metadata`,
        {
          title: editForm.title,
          module_name: editForm.module_name,
          tags: tagsArr,
          // Vincent B629 (Eric 2026-07-30 SPEC STRICK PROD) — Sauvegarde
          // du thème de formation modifié inline.
          training_theme: editForm.training_theme || "general",
        },
        { withCredentials: true }
      );
      // Vincent (2026-02) — Persistance catégorie + parcours via helpers
      // partagés (lib/videoEditPersistence.js). La logique reste identique
      // mais est désormais mutualisée avec VideoDetailPage.
      const originalVideo = videos.find((x) => x.id === videoId);
      await saveCategoryIfChanged(
        API, videoId,
        originalVideo?.category_id || "",
        editForm.category_id || "",
      );
      await savePathwayDiff(
        API, videoId, allPathways,
        pathwayIdsForVideo(allPathways, videoId),
        editForm.pathway_ids || new Set(),
      );
      toast.success(tr("Vidéo mise à jour", "Video updated"));
      setEditingId(null);
      // Tâche 1171 (Eric 2026-06-25) — Après « Sauver », l'interface restait
      // au niveau du fichier : le rechargement de la liste remontait la page
      // tout en haut et on perdait des yeux le fichier qu'on venait d'éditer.
      const keepY = window.scrollY;
      await loadAll();
      requestAnimationFrame(() => {
        const row = document.querySelector(`[data-testid="video-row-${videoId}"]`);
        if (row) {
          row.scrollIntoView({ block: "center" });
        } else {
          window.scrollTo({ top: keepY });
        }
      });
    } catch (e) {
      console.error("[saveEdit]", e);
      toast.error(explainError(e, tr("Échec de la sauvegarde", "Save failed")), { duration: 8000 });
    }
  };

  // Vincent B704 résiduel (Eric 2026-02-10) — refonte défensive suite au
  // rapport « le 1er clic ne fait rien, seuls les clics suivants marchent ».
  // Cause probable : le pattern précédent (objet `{[mod]: bool}` avec défaut
  // ouvert via `!== false`) laissait 3 états possibles (undefined/true/false)
  // et créait une ambigüité entre l'état visible et l'état stocké.
  //
  // Nouveau pattern : Set des chemins COLLAPSÉS (2 états stricts : dans le
  // Set = fermé, hors du Set = ouvert). Plus rien d'ambigu → 1er clic
  // toggle systématiquement l'état visible.
  //
  // Persistance sessionStorage (Eric 2026-02-10 — suggestion Vincent) :
  // l'utilisateur retrouve son arbre replié tel qu'il l'a laissé au retour
  // sur l'Explorateur (sans repartir de zéro à chaque navigation). Isolé
  // par onglet (`videos` / `texts`) pour éviter les collisions.
  const _storageKey = "knoria.explorer.collapsedPaths.v1";
  const [collapsedPaths, setCollapsedPaths] = useState(() => {
    try {
      const raw = sessionStorage.getItem(_storageKey);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch { return new Set(); }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(_storageKey, JSON.stringify([...collapsedPaths]));
    } catch { /* quota / private mode : silent fail */ }
  }, [collapsedPaths]);

  const toggleModule = useCallback((mod, e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(mod)) next.delete(mod);
      else next.add(mod);
      return next;
    });
  }, []);

  // Build a recursive tree from flat {moduleName: [videos]} map
  // Path separator is " / " (with spaces) — matches the convention used across the app.
  // Videos whose module is exactly "Sans module" (no path) stay at the root.
  const buildTree = (flatGrouped) => {
    const root = { name: "__root__", fullPath: "", children: new Map(), videos: [] };
    Object.entries(flatGrouped).forEach(([modName, vids]) => {
      const parts = modName === "Sans module" ? ["Sans module"] : modName.split(" / ").filter(Boolean);
      if (parts.length === 0) { root.videos.push(...vids); return; }
      let cursor = root;
      let accPath = "";
      parts.forEach((part, idx) => {
        accPath = accPath ? `${accPath} / ${part}` : part;
        if (!cursor.children.has(part)) {
          cursor.children.set(part, { name: part, fullPath: accPath, children: new Map(), videos: [] });
        }
        cursor = cursor.children.get(part);
        if (idx === parts.length - 1) cursor.videos.push(...vids);
      });
    });
    return root;
  };

  // Count total descendant videos (for the badge)
  const countAll = (node) => {
    let c = node.videos.length;
    node.children.forEach((ch) => { c += countAll(ch); });
    return c;
  };

  const tree = buildTree(visibleGrouped);

  const totalVideos = videos.length;
  const visibleCount = Object.values(visibleGrouped).reduce((sum, arr) => sum + arr.length, 0);

  // Vincent #137 — Map vidéos par id pour passer à l'onglet Connaissances.
  const videosById = useMemo(() => {
    const m = {};
    videos.forEach(v => { m[v.id] = v; });
    return m;
  }, [videos]);

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="explorer-title">
            <FolderOpen size={28} className="text-[#002FA7]" weight="duotone" />
            {tr("Explorateur de fichiers", "File Explorer")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {activeTab === "videos"
              ? tr("Organisez vos médias vidéo et audio (.mp4, .mp3) par modules, ajoutez des tags, renommez en 1 clic", "Organize your video and audio media (.mp4, .mp3) by module, add tags, rename in 1 click")
              : activeTab === "texts"
              ? tr(`Organisez vos documents textuels importés (${LISTE_DOC}) par modules, ajoutez des tags, renommez en 1 clic`, `Organize your imported text documents (${LISTE_DOC}) by module, add tags, rename in 1 click`)
              : tr("Tous les fichiers (vidéo, audio et documents) de votre organisation", "Every file (video, audio and documents) of your organization")}
          </p>
          {/* Tâche 1278 (Eric 2026-06-29) — L'injection des passages manqués
              sur TOUTE la bibliothèque, depuis l'endroit d'où on gère les
              fichiers. Réservée aux administrateurs : elle écrit dans les
              transcriptions. */}
          {activeTab === "videos" && isAdmin && (
            <div className="mt-3" data-testid="explorer-bulk-gap-inject">
              <BulkGapInjectButtons />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {/* Eric 2026-09-08 — Arrêt groupé : quand une panne bloque dix
              vidéos ensemble, les arrêter une par une laisse les dernières
              consommer des crédits pendant qu'on clique. */}
          {(activeTab === "videos" || activeTab === "texts") && isAdmin && (
            <button
              onClick={doForceStopBulk}
              disabled={bulkStopBusy}
              className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 ${
                selectedVideoIds.length > 0
                  ? "bg-red-700 text-white hover:bg-red-800"
                  : "bg-zinc-200 text-zinc-500 hover:bg-zinc-300"
              }`}
              data-testid="btn-force-stop-bulk"
              title={tr("Forcer l'arrêt du pipeline sur toutes les vidéos sélectionnées", "Force-stop the pipeline on every selected video")}
            >
              <Prohibit size={13} weight="bold" />
              {bulkStopBusy ? tr("Arrêt…", "Stopping…") : tr("Forcer l'arrêt", "Force stop")}
              {selectedVideoIds.length > 0 && (
                <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px] font-mono">
                  {selectedVideoIds.length}
                </span>
              )}
            </button>
          )}
          {/* Vincent #163 — Bouton "Assigner à parcours" actif si au moins 1 sélection */}
          {(activeTab === "videos" || activeTab === "texts") && isAdmin && (
            <button
              onClick={() => {
                if (selectedVideoIds.length === 0) {
                  toast.warning(`⚠️ Aucune vidéo sélectionnée. Cochez d'abord les vidéos à inclure dans le parcours.`);
                  return;
                }
                setShowAssignModal(true);
              }}
              className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 ${
                selectedVideoIds.length > 0
                  ? "bg-[#002FA7] text-white hover:bg-[#001a5c]"
                  : "bg-zinc-200 text-zinc-500 hover:bg-zinc-300"
              }`}
              data-testid="btn-assign-to-pathway"
            >
              📚 {tr("Assigner à un parcours", "Assign to a pathway")}
              {selectedVideoIds.length > 0 && (
                <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px] font-mono">
                  {selectedVideoIds.length}
                </span>
              )}
            </button>
          )}
          {(activeTab === "videos" || activeTab === "texts") && isAdmin && (
            <button
              onClick={() => {
                if (selectedVideoIds.length === 0) {
                  toast.warning(tr(
                    "⚠️ Aucun élément sélectionné. Cochez d'abord les documents ou vidéos à étiqueter.",
                    "⚠️ Nothing selected. Tick the documents or videos to tag first.",
                  ));
                  return;
                }
                setShowBatchTagsModal(true);
              }}
              className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 ${
                selectedVideoIds.length > 0
                  ? "bg-teal-600 text-white hover:bg-teal-700"
                  : "bg-zinc-200 text-zinc-500 hover:bg-zinc-300"
              }`}
              data-testid="btn-bulk-tag-selection"
              title={tr("Appliquer un ou plusieurs tags (vocabulaire org) sur toute la sélection", "Apply one or more tags (org vocabulary) to the whole selection")}
            >
              🏷️ {tr("Étiqueter la sélection", "Tag the selection")}
              {selectedVideoIds.length > 0 && (
                <span className="bg-white/25 px-1.5 py-0.5 rounded text-[10px] font-mono">
                  {selectedVideoIds.length}
                </span>
              )}
            </button>
          )}
          {/* Eric (2026-06-14) — LOT F : Génération Q&R en BULK depuis l'onglet
              "Textes" (sélection multi + lancement séquentiel en arrière-plan).
              Permet de générer la KB complète d'une session d'onboarding en un
              seul clic après l'import de N procédures Word. */}
          {(activeTab === "texts") && isAdmin && (
            <button
              onClick={async () => {
                if (selectedVideoIds.length === 0) {
                  toast.warning(tr(
                    "⚠️ Aucun fichier sélectionné. Cochez d'abord les documents à traiter.",
                    "⚠️ No file selected. Tick the documents to process first.",
                  ));
                  return;
                }
                // Tâche 1435 — Les sources de référence (page web, image) ne
                // produisent que titre + étiquettes + synthèse. Les envoyer au
                // pipeline Q&R ferait compter des erreurs là où le serveur
                // applique une règle voulue : on les ÉCARTE et on le dit.
                const eligibles = selectedVideoIds.filter((did) => {
                  const doc = videos.find((v) => v.id === did);
                  return !(doc?.connaissances_autorisees?.length > 0);
                });
                const ecartes = selectedVideoIds.length - eligibles.length;
                if (eligibles.length === 0) {
                  toast.warning(tr(
                    "Aucun document éligible : les pages web et les images ne génèrent pas de Q&R.",
                    "No eligible document: web pages and images do not generate Q&A.",
                  ));
                  return;
                }
                const ok = await confirmDialog(`Lancer la génération Q&R sur ${eligibles.length} document(s) ? Le pipeline tourne en arrière-plan (1-3 min par document).`);
                if (!ok) return;
                let done = 0, ko = 0;
                toast.info(`Pipeline Q&R lancé sur ${eligibles.length} document(s)…`);
                for (const did of eligibles) {
                  try {
                    await axios.post(`${API}/api/agent/generate-qa-batch/${did}?background=1`, {}, { withCredentials: true });
                    done++;
                  } catch (_) { ko++; }
                }
                const note = ecartes > 0
                  ? tr(` · ${ecartes} source(s) de référence écartée(s)`, ` · ${ecartes} reference source(s) skipped`)
                  : "";
                if (ko === 0) toast.success(`✅ Q&R lancée pour ${done} document(s)${note}. Suivre la progression sur chaque document.`);
                else toast.warning(`${done} OK · ${ko} en erreur${note}. Voir les documents concernés.`);
              }}
              className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 ${
                selectedVideoIds.length > 0
                  ? "bg-violet-600 text-white hover:bg-violet-700"
                  : "bg-zinc-200 text-zinc-500 hover:bg-zinc-300"
              }`}
              data-testid="btn-bulk-generate-qa-texts"
              title={tr("Lancer la génération Q&R en arrière-plan sur tous les documents sélectionnés", "Start the Q&A generation in the background on every selected document")}
            >
              ⚗️ {tr("Générer Q&R sur la sélection", "Generate Q&A on the selection")}
              {selectedVideoIds.length > 0 && (
                <span className="bg-white/25 px-1.5 py-0.5 rounded text-[10px] font-mono">
                  {selectedVideoIds.length}
                </span>
              )}
            </button>
          )}
          {/* Vincent B979 (Eric 2026-02-26 SPEC STRICK PROD) — Bulk
              re-merge fusion sur la sélection. Visible sur l'onglet
              Vidéos (pas Textes). Utile combiné avec le filtre
              « ⚠️ Couverture faible » qui met les pires en tête. */}
          {(activeTab === "videos") && isAdmin && (
            <button
              onClick={async () => {
                if (selectedVideoIds.length === 0) {
                  toast.warning(tr(
                    "⚠️ Aucune vidéo sélectionnée. Cochez d'abord les vidéos à re-fusionner (astuce : active le filtre « Couverture faible » pour cibler les pires).",
                    "⚠️ No video selected. Tick the videos to merge again (tip: turn on the “Low coverage” filter to target the worst ones).",
                  ));
                  return;
                }
                const ok = await confirmDialog(
                  tr(
                    `Relancer la fusion Whisper + Fireflies sur ${selectedVideoIds.length} vidéo(s) sélectionnée(s) ? ` +
                    `Opération lourde (crédits GPT-5.2) ; un snapshot est pris avant chaque re-fusion (Undo via historique). ` +
                    `Les vidéos sans Whisper ou sans Fireflies sont ignorées.`,
                    `Run the Whisper + Fireflies merge again on ${selectedVideoIds.length} selected video(s)? ` +
                    `Heavy operation (GPT-5.2 credits); a snapshot is taken before each re-merge (undo from the history). ` +
                    `Videos without Whisper or without Fireflies are skipped.`,
                  ),
                  { tone: "warning", confirmLabel: tr("Relancer la fusion", "Run the merge again") }
                );
                if (!ok) return;
                setBulkRemergingBusy(true);
                try {
                  const { data } = await axios.post(
                    `${API}/api/admin/videos/bulk-rerun-fusion`,
                    { video_ids: selectedVideoIds },
                    { withCredentials: true, timeout: 120000 },
                  );
                  const launched = data?.total_launched ?? 0;
                  const skipped = data?.total_skipped ?? 0;
                  if (launched === 0) {
                    toast.warning(`Aucune fusion relancée — ${skipped} vidéo(s) skippée(s). Vérifie qu'elles ont bien Whisper + Fireflies.`);
                  } else if (skipped > 0) {
                    toast.success(`✅ ${launched} fusion(s) relancée(s) en arrière-plan (${skipped} skippée(s)). Suivi via l'onglet Fusionnée de chaque vidéo.`);
                  } else {
                    toast.success(`✅ ${launched} fusion(s) relancée(s) en arrière-plan. Suivi via l'onglet Fusionnée de chaque vidéo.`);
                  }
                } catch (err) {
                  toast.error(tr("Erreur : ", "Error: ") + (err?.response?.data?.detail || err.message));
                } finally {
                  setBulkRemergingBusy(false);
                }
              }}
              disabled={bulkRemergingBusy}
              className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 ${
                selectedVideoIds.length > 0
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : "bg-zinc-200 text-zinc-500 hover:bg-zinc-300"
              }`}
              data-testid="btn-bulk-rerun-fusion"
              title={tr("Relancer la fusion Whisper+Fireflies sur toutes les vidéos sélectionnées. Combine avec le filtre « Couverture faible » pour cibler les pires.", "Run the Whisper+Fireflies merge again on every selected video. Combine with the “Low coverage” filter to target the worst ones.")}
            >
              {bulkRemergingBusy ? tr("⏳ Fusion en cours…", "⏳ Merge running…") : tr("🔀 Refaire fusion sur sélection", "🔀 Re-run merge on selection")}
              {selectedVideoIds.length > 0 && !bulkRemergingBusy && (
                <span className="bg-white/25 px-1.5 py-0.5 rounded text-[10px] font-mono">
                  {selectedVideoIds.length}
                </span>
              )}
            </button>
          )}
          {/* Tâche 1261 (Eric 2026-06-28) — COPIE FIDÈLE EN LOT. La copie
              mono-source n'existait que vidéo par vidéo ; ici on l'applique
              d'un coup à toute la sélection, et les vidéos dont la source n'a
              pas changé depuis la dernière fusion sont ignorées. */}
          {(activeTab === "videos") && isAdmin && (
            <div className="relative inline-block">
              <button
                onClick={() => setBulkCopyMenuOpen((open) => !open)}
                disabled={bulkRemergingBusy}
                className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 ${
                  selectedVideoIds.length > 0
                    ? "bg-violet-600 text-white hover:bg-violet-700"
                    : "bg-zinc-200 text-zinc-500 hover:bg-zinc-300"
                }`}
                data-testid="btn-bulk-copy-fusion"
                title={tr("Copier une seule transcription telle quelle dans la version fusionnée, sur toutes les vidéos sélectionnées. Aucun appel IA.", "Copy a single transcript as-is into the merged version, on every selected video. No AI call.")}
              >
                📋 {tr("Copie fidèle sur sélection", "Faithful copy on selection")} ▾
                {selectedVideoIds.length > 0 && !bulkRemergingBusy && (
                  <span className="bg-white/25 px-1.5 py-0.5 rounded text-[10px] font-mono">
                    {selectedVideoIds.length}
                  </span>
                )}
              </button>
              {bulkCopyMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setBulkCopyMenuOpen(false)} />
                  <div
                    className="absolute left-0 top-full mt-1 z-50 w-72 rounded-md border border-zinc-200 bg-white shadow-lg py-1 text-left"
                    data-testid="bulk-copy-menu"
                  >
                    {[
                      ["fireflies", tr("Copie fidèle — Fireflies seul", "Faithful copy — Fireflies only"),
                       tr("Reprend Fireflies à l'identique. Les vidéos sans Fireflies sont ignorées.", "Takes Fireflies verbatim. Videos without Fireflies are skipped.")],
                      ["whisper", tr("Copie fidèle — Whisper seul", "Faithful copy — Whisper only"),
                       tr("Reprend Whisper à l'identique. Les vidéos sans Whisper sont ignorées.", "Takes Whisper verbatim. Videos without Whisper are skipped.")],
                    ].map(([source, label, hint]) => (
                      <button
                        key={source}
                        type="button"
                        onClick={() => runBulkCopy(source)}
                        className="w-full px-3 py-2 text-left hover:bg-zinc-50"
                        data-testid={`bulk-copy-mode-${source}`}
                        title={hint}
                      >
                        <span className="block text-xs font-semibold text-zinc-900">{label}</span>
                        <span className="block text-[11px] text-zinc-500 leading-snug mt-0.5">{hint}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {(activeTab === "videos" || activeTab === "texts") && (
            <>
              <span className="px-3 py-1.5 bg-secondary rounded-lg" data-testid="stat-total">
                <strong>{visibleCount}</strong> / {totalVideos} {activeTab === "texts" ? tr("fichiers", "files") : tr("vidéos", "videos")}
              </span>
              <span className="px-3 py-1.5 bg-secondary rounded-lg" data-testid="stat-modules">
                <strong>{modules.length}</strong> {tr("modules", "modules")}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Vincent B981 (Eric 2026-02-26 SPEC STRICK PROD) — Barre de
          progression live pour le bulk rerun fusion. Visible tant qu'un
          job est en cours (status queued/running). Reste 30s après
          completion pour laisser le temps de lire le rapport final. */}
      {bulkRerunProgress && bulkRerunProgress.status !== "idle" && (bulkRerunProgress.total || 0) > 0 && (
        <div
          className={`mb-4 border rounded-lg p-3 ${
            bulkRerunProgress.status === "done"
              ? "bg-green-50 border-green-300"
              : "bg-purple-50 border-purple-300"
          }`}
          data-testid="bulk-rerun-progress-bar"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-zinc-800">
              {bulkRerunProgress.status === "done"
                ? tr("✅ Fusion terminée", "✅ Merge done")
                : bulkRerunProgress.status === "running"
                  ? tr("🔀 Fusion en cours…", "🔀 Merge running…")
                  : tr("⏳ En attente…", "⏳ Waiting…")}
            </div>
            <div className="text-xs font-mono text-zinc-700" data-testid="bulk-rerun-progress-counter">
              {bulkRerunProgress.done || 0} / {bulkRerunProgress.total || 0}
            </div>
          </div>
          {/* Barre de progression */}
          <div className="w-full h-2 bg-white/70 border border-zinc-300 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                bulkRerunProgress.status === "done" ? "bg-green-500" : "bg-purple-500"
              }`}
              style={{
                width: `${Math.min(100, Math.round(((bulkRerunProgress.done || 0) / Math.max(1, bulkRerunProgress.total || 1)) * 100))}%`,
              }}
              data-testid="bulk-rerun-progress-fill"
            />
          </div>
          {/* Vidéo en cours */}
          {bulkRerunProgress.current_title && bulkRerunProgress.status === "running" && (
            <div className="mt-2 text-xs text-zinc-700 truncate" data-testid="bulk-rerun-progress-current">
              📹 En cours :{" "}
              <span className="font-semibold text-purple-800">
                {bulkRerunProgress.current_title}
              </span>
            </div>
          )}
          {bulkRerunProgress.status === "done" && (
            <div className="mt-2 text-xs text-green-800">
              {tr("Toutes les fusions sont terminées. Recharge la page pour voir les nouveaux ratios de couverture.", "Every merge is done. Reload the page to see the new coverage ratios.")}
            </div>
          )}
        </div>
      )}

      {/* Vincent #137 (Eric 2026-05-16) — Tab switcher Vidéos / Connaissances
          Vincent #303 (2026-06-12) — Ajout de l'onglet "Textes" après "Vidéos". */}
      <div className="flex border-b border-border mb-5">
        <button
          onClick={() => setActiveTab("videos")}
          className={`relative px-4 py-2 text-sm font-semibold transition-colors ${
            activeTab === "videos" ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"
          }`}
          data-testid="explorer-tab-videos"
        >
          <span className="inline-flex items-center gap-2">
            <FileVideo size={15} weight="duotone" /> {tr("Vidéos", "Videos")}
          </span>
          {activeTab === "videos" && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#002FA7] rounded-full" />}
        </button>
        <button
          onClick={() => setActiveTab("texts")}
          className={`relative px-4 py-2 text-sm font-semibold transition-colors ${
            activeTab === "texts" ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"
          }`}
          data-testid="explorer-tab-texts"
        >
          <span className="inline-flex items-center gap-2">
            <FileVideo size={15} weight="duotone" /> {tr("Textes", "Texts")}
          </span>
          {activeTab === "texts" && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#002FA7] rounded-full" />}
        </button>
        {/* Vincent #348 (Eric 2026-06-18 SPEC STRICK ULTRA) — Onglet "Connaissances"
            RETIRÉ de l'Explorateur de fichiers. Les connaissances sont déjà
            accessibles via le menu Base de connaissances. */}
      </div>

      {false ? (
        <KnowledgeExplorerTab videosById={videosById} />
      ) : (
      <>
      {/* Vincent B930 (Eric 2026-02-14 SPEC STRICK PROD) — Zone bulk
          import documents textuels (drag-and-drop + auto Q&R + auto
          suggestion de tags). Visible uniquement sur l'onglet Textes
          pour les éditeurs. Fermée par défaut pour ne pas pousser
          la liste des documents hors écran ; s'ouvre automatiquement
          quand un fichier est déposé. */}
      {activeTab === "texts" && isAdmin && (
        <TextDocsBulkImportZone
          pathways={allPathways}
          categories={categories}
          onUploaded={loadAll}
        />
      )}
      {/* Tâche 1438 (Eric 2026-10-02) — Sous la zone d'import, et replié par
          défaut : on ne regarde les refus que quand on en cherche la cause.
          Tâche 1451 — le journal des VIDÉOS existe aussi, et chacun garde son
          propre réglage de conservation : les deux n'ont ni le même volume ni
          la même durée d'intérêt. */}
      {activeTab === "texts" && isAdmin && <ImportRefusalsPanel kind="document" />}
      {activeTab === "videos" && isAdmin && <ImportRefusalsPanel kind="video" />}
      {/* Vincent B904 (Eric 2026-02-14 SPEC STRICK PROD) — Barre de
          recherche par TAG au-dessus des filtres classiques. Autocomplete
          sur les tags org existants, mode ET/OU, résultats cross-KB. */}
      <div className="mb-3">
        <TagSearchBar allTags={allTags} />
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[200px] relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAdmin
              ? tr("Filtrer cet explorateur (titre, identifiant, tag ou contenu)...", "Filter this explorer (title, identifier, tag or content)...")
              : tr("Filtrer cet explorateur (titre, tag ou contenu)...", "Filter this explorer (title, tag or content)...")}
            className="w-full pl-10 pr-3 py-2 bg-card border border-border rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            data-testid="input-search-explorer"
          />
        </div>
        <select
          value={filterModule}
          onChange={(e) => setFilterModule(e.target.value)}
          className="px-3 py-2 bg-card border border-border rounded-lg text-sm"
          data-testid="select-module-filter"
        >
          <option value="all">{tr(`Tous les modules (${modules.length})`, `All modules (${modules.length})`)}</option>
          {modules.map((m) => (
            <option key={m.name} value={m.name}>{`${m.name} (${m.count})`}</option>
          ))}
        </select>
        <select
          value={filterTag}
          onChange={(e) => setFilterTag(e.target.value)}
          className="px-3 py-2 bg-card border border-border rounded-lg text-sm"
          data-testid="select-tag-filter"
        >
          <option value="all">{tr(`Tous les tags (${allTags.length})`, `All tags (${allTags.length})`)}</option>
          {/* Vincent XLSX 2026-07-09 #11 (Eric 2026-07-09 SPEC STRICK) — Tri
              alphabétique insensible à la casse (CELIAPP, REER, celiapp… mêlés). */}
          {[...allTags].sort((a, b) => String(a).toLowerCase().localeCompare(String(b).toLowerCase(), "fr", { sensitivity: "base" })).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 bg-card border border-border rounded-lg text-sm"
          data-testid="select-sort-by"
          title={tr("Trier les vidéos", "Sort the videos")}
        >
          <option value="date_desc">{tr("Date ↓ (récent → ancien)", "Date ↓ (newest → oldest)")}</option>
          <option value="date_asc">{tr("Date ↑ (ancien → récent)", "Date ↑ (oldest → newest)")}</option>
          <option value="name_asc">{tr("Nom A → Z", "Name A → Z")}</option>
          <option value="name_desc">{tr("Nom Z → A", "Name Z → A")}</option>
          {/* Vincent #65 batch FLHQ (Eric 2026-02-06) — Tri par catégorie */}
          <option value="category_asc">{tr("Catégorie A → Z", "Category A → Z")}</option>
          <option value="category_desc">{tr("Catégorie Z → A", "Category Z → A")}</option>
          {/* Vincent B894 (Eric 2026-02-14 SPEC STRICK PROD) — Tri par
              couverture croissante : les vidéos ayant perdu le plus de
              contenu à la fusion remontent en tête (les plus urgentes). */}
          <option value="coverage_asc" data-testid="sort-coverage-asc">⚠️ Couverture faible d&apos;abord</option>
        </select>
        {/* Vincent B894 — Chip filter « n'afficher QUE les vidéos à couverture
            faible » (content_coverage_alert === true selon B885). Bénéfique
            pour un triage batch : Vincent voit immédiatement les vidéos
            à corriger en priorité. */}
        {Object.values(coverageMap).some(c => c.coverage_alert) && (
          <button
            type="button"
            onClick={() => setLowCoverageOnly(v => !v)}
            className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
              lowCoverageOnly
                ? "bg-amber-600 text-white border-amber-700 hover:bg-amber-700"
                : "bg-white border-amber-300 text-amber-800 hover:bg-amber-50"
            }`}
            title={
              lowCoverageOnly
                ? tr("Désactiver le filtre — afficher toutes les vidéos", "Turn the filter off — show every video")
                : tr("Afficher seulement les vidéos à couverture faible (ratio < 90 %)", "Show only videos with low coverage (ratio < 90%)")
            }
            data-testid="filter-low-coverage-toggle"
            aria-pressed={lowCoverageOnly}
          >
            ⚠️ {lowCoverageOnly ? tr("Couverture faible actif", "Low coverage on") : tr("Couverture faible", "Low coverage")}
            <span className="ml-1 opacity-75" data-testid="filter-low-coverage-count">
              ({Object.values(coverageMap).filter(c => c.coverage_alert).length})
            </span>
          </button>
        )}
        {/* Tâche 1351 (Eric 2026-06-30) — Vue d'avancement. Les pastilles
            disent ce qu'on filtre, pas où on en est : ce compte rendu répond
            à « combien reste-t-il ? » sans ouvrir une seule fiche. Calculé
            depuis la liste déjà chargée, donc aucun appel de plus. */}
        <span
          className="text-xs text-zinc-600 px-2.5 py-1.5 rounded-md border border-zinc-200 bg-zinc-50"
          data-testid="explorer-progress-summary"
          data-total={String(videos.length)}
          data-to-verify={String(videos.filter(v => !v.web_verified).length)}
          data-to-dictionary={String(videos.filter(v => !v.dictionary_generated).length)}
          title={tr(
            "Avancement de la relecture : vérification web de la synthèse et passage au dictionnaire.",
            "Review progress: web verification of the synthesis and dictionary pass.",
          )}
        >
          {tr(
            `${videos.length} vidéos · ${videos.filter(v => !v.web_verified).length} à vérifier · ${videos.filter(v => !v.dictionary_generated).length} à mettre au dictionnaire`,
            `${videos.length} videos · ${videos.filter(v => !v.web_verified).length} to verify · ${videos.filter(v => !v.dictionary_generated).length} to add to dictionary`,
          )}
        </span>
        {/* Tâche 1341 (Eric 2026-06-30) — Filtre « Non vérifiées ». Le
            compteur compte les vidéos de l'onglet courant dont la synthèse
            n'a pas encore été vérifiée sur le web. */}
        <button
          type="button"
          onClick={() => setNotVerifiedOnly(v => !v)}
          className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
            notVerifiedOnly
              ? "bg-emerald-700 text-white border-emerald-800 hover:bg-emerald-800"
              : "bg-white border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          }`}
          title={
            notVerifiedOnly
              ? tr("Désactiver le filtre — afficher toutes les vidéos", "Turn the filter off — show every video")
              : tr("Afficher seulement les vidéos dont la synthèse n'est pas encore vérifiée sur le web", "Show only videos whose synthesis is not web-verified yet")
          }
          data-testid="filter-not-verified-toggle"
          aria-pressed={notVerifiedOnly}
        >
          {notVerifiedOnly ? tr("Non vérifiées actif", "Unverified on") : tr("Non vérifiées", "Unverified")}
          <span className="ml-1 opacity-75" data-testid="filter-not-verified-count">
            ({videos.filter(v => !v.web_verified).length})
          </span>
        </button>
        {isAdmin && (
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              const name = await promptDialog(
                tr("Nom de la nouvelle catégorie ?", "Name of the new category?"),
                { placeholder: tr("Ex: Coaching, Vente…", "e.g. Coaching, Sales…"), confirmLabel: tr("Créer", "Create") },
              );
              if (!name || !name.trim()) return;
              const icon = await promptDialog(
                tr("Icône (emoji optionnel) :", "Icon (optional emoji):"),
                { defaultValue: "📚", placeholder: "📚", confirmLabel: tr("Créer la catégorie", "Create the category") },
              ) || "";
              try {
                const { data } = await axios.post(
                  `${API}/api/content-categories`,
                  { name: name.trim(), description: "", icon, color: "#002FA7", order: categories.length },
                  { withCredentials: true }
                );
                setCategories((prev) => [...prev, data]);
                toast.success(tr(`Catégorie « ${data.name} » créée`, `Category “${data.name}” created`));
              } catch (err) {
                toast.error(explainError(err, tr("Échec de création de la catégorie", "Could not create the category")));
              }
            }}
            data-testid="create-category-btn"
            title={tr("Créer une nouvelle catégorie de contenu", "Create a new content category")}
          >
            <Plus size={14} className="mr-1" />
            {categories.length === 0 ? tr("Créer une catégorie", "Create a category") : tr("+ Catégorie", "+ Category")}
          </Button>
        )}
      </div>

      {/* Tâche 1305 (Eric 2026-06-30) — Les derniers identifiants cherchés,
          sous les filtres, pour y revenir en un clic. Réservés aux éditeurs
          (ce sont eux qui cherchent par identifiant). */}
      {isAdmin && (
        <div className="-mt-4 mb-5">
          <RecentIdSearches
            items={recentIdSearches}
            onPick={(entry) => setSearch(entry.id)}
            onForget={(id) => setRecentIdSearches(forgetIdSearch(id))}
            testidPrefix="explorer-recent-id"
          />
        </div>
      )}

      {/* Folder tree */}
      {loading ? (
        <div className="text-center py-16 text-muted-foreground">
          <SpinnerGap size={32} className="animate-spin mx-auto mb-3" />
          Chargement...
        </div>
      ) : Object.keys(visibleGrouped).length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Folder size={48} weight="duotone" className="mx-auto mb-3 opacity-30" />
          <p className="font-semibold">{tr("Aucune vidéo trouvée", "No video found")}</p>
          <p className="text-sm mt-1">{tr("Modifiez vos filtres ou téléversez une nouvelle vidéo.", "Change your filters or upload a new video.")}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="folder-tree">
          {(() => {
            const renderVideoRow = (v) => (
              <div key={v.id} className="px-4 py-3 hover:bg-secondary/30 transition-colors flex items-start gap-2" data-testid={`video-row-${v.id}`}>
                {/* Vincent #163 — Case à cocher (sélection multiple) + dropdown checklist par vidéo */}
                {isAdmin && (
                  <input
                    type="checkbox"
                    checked={selectedVideoIds.includes(v.id)}
                    onChange={() => toggleSelectVideo(v.id)}
                    className="mt-1 w-4 h-4 accent-[#002FA7] flex-shrink-0"
                    data-testid={`select-video-${v.id}`}
                    title={tr("Sélectionner pour assigner à un parcours", "Select to assign to a pathway")}
                  />
                )}
                <div className="flex-1 min-w-0">
                {editingId === v.id ? (
                  <div className="space-y-2">
                    <input
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="w-full px-2 py-1.5 bg-background border border-border rounded text-sm"
                      placeholder="Titre"
                      data-testid={`input-title-${v.id}`}
                    />
                    {/* Vincent B632 (Eric 2026-07-31 SPEC STRICK PROD) —
                        Réorganisation stricte du formulaire d'édition inline :
                          Ligne 1 : [Thème + select]  |  [Catégorie + select]
                          Ligne 2 : [Chemin + input]  |  [Étiquettes + input]
                          Ligne 3 : Liste des parcours associés
                          Ligne 4 : Liste complète des étiquettes cliquables
                        Chaque select/input est précédé d'un label court en
                        style « uppercase tracking-wider » pour repérage
                        visuel rapide. */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Ligne 1 col 1 : Thème */}
                      <div className="flex items-center gap-2 min-w-0">
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold whitespace-nowrap" htmlFor={`input-theme-${v.id}`}>
                          {tr("Thème", "Theme")}
                        </label>
                        <select
                          id={`input-theme-${v.id}`}
                          value={editForm.training_theme || "general"}
                          onChange={(e) => setEditForm({ ...editForm, training_theme: e.target.value })}
                          className="flex-1 min-w-0 px-2 py-1.5 bg-background border border-border rounded text-xs"
                          data-testid={`input-training-theme-${v.id}`}
                          title={tr("Thème de formation du fichier", "Training theme of the file")}
                        >
                          {/* Tâche 1157 (Eric 2026-06-17) — Ordre
                              alphabétique : chercher « Retraite » dans une
                              liste rangée par ordre d'ajout fait perdre du
                              temps à chaque fichier. */}
                          {[...TRAINING_THEMES]
                            .sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), "fr", { sensitivity: "base" }))
                            .map((t) => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                      </div>
                      {/* Ligne 1 col 2 : Catégorie */}
                      <div className="flex items-center gap-2 min-w-0">
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold whitespace-nowrap" htmlFor={`input-category-${v.id}`}>
                          {tr("Catégorie", "Category")}
                        </label>
                        {categories.length > 0 ? (
                          <select
                            id={`input-category-${v.id}`}
                            value={editForm.category_id || ""}
                            onChange={(e) => setEditForm({ ...editForm, category_id: e.target.value })}
                            className="flex-1 min-w-0 px-2 py-1.5 bg-background border border-border rounded text-xs"
                            title={tr("Catégorie du fichier (gérée depuis « Catégories de fichiers » — la même liste pour les vidéos et les documents).", "File category (managed from “File categories” — the same list for videos and documents).")}
                            data-testid={`input-category-${v.id}`}
                          >
                            <option value="">{tr("— Aucune —", "— None —")}</option>
                            {[...categories]
                              .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" }))
                              .map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                          </select>
                        ) : (
                          <span className="text-[10px] text-zinc-400 italic">{tr("Aucune catégorie configurée", "No category configured")}</span>
                        )}
                      </div>
                      {/* Ligne 2 col 1 : Chemin (module_name) */}
                      <div className="flex items-center gap-2 min-w-0">
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold whitespace-nowrap" htmlFor={`input-module-${v.id}`}>
                          Chemin
                        </label>
                        <input
                          id={`input-module-${v.id}`}
                          value={editForm.module_name}
                          onChange={(e) => setEditForm({ ...editForm, module_name: e.target.value })}
                          list={`modules-list-${v.id}`}
                          className="flex-1 min-w-0 px-2 py-1.5 bg-background border border-border rounded text-xs"
                          placeholder={tr("ex : Investissement / 1. Introduction", "e.g. Investing / 1. Introduction")}
                          title={tr(
                            'Astuce : utilisez " / " pour créer une arborescence. Exemple : Formation générale / Module Recrutement / Introduction',
                            'Tip: use " / " to build a tree. Example: General training / Recruiting module / Introduction',
                          )}
                          data-testid={`input-module-${v.id}`}
                        />
                        <datalist id={`modules-list-${v.id}`}>
                          {/* Tâche 1157 — Chemins proposés par ordre alphabétique. */}
                          {[...modules]
                            .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" }))
                            .map((m) => <option key={m.name} value={m.name} />)}
                        </datalist>
                      </div>
                      {/* Ligne 2 col 2 : Étiquettes (tags) */}
                      <div className="flex items-center gap-2 min-w-0">
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold whitespace-nowrap" htmlFor={`input-tags-${v.id}`}>
                          {tr("Étiquettes", "Tags")}
                        </label>
                        <input
                          id={`input-tags-${v.id}`}
                          value={editForm.tags}
                          onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                          list={`tags-list-${v.id}`}
                          className="flex-1 min-w-0 px-2 py-1.5 bg-background border border-border rounded text-xs"
                          placeholder={tr("ex : recrutement, intro", "e.g. recruiting, intro")}
                          title={tr(
                            "Étiquettes séparées par virgule. Suggestions auto-complétées depuis les étiquettes existantes de votre organisation.",
                            "Comma-separated tags. Suggestions are auto-completed from your organisation's existing tags.",
                          )}
                          data-testid={`input-tags-${v.id}`}
                        />
                        <datalist id={`tags-list-${v.id}`}>
                          {allTags.map((tag) => <option key={tag} value={tag} />)}
                        </datalist>
                      </div>
                    </div>
                    {/* Vincent B902 (Eric 2026-02-14 SPEC STRICK PROD) —
                        Bandeau tags suggérés (LLM + TF-IDF hybride) qui
                        apparaît au-dessus du bloc parcours. Accept / reject
                        1 par 1 ou en bulk. Fonctionne pour vidéos ET
                        textes (is_text_doc). */}
                    <TagSuggestionsBar
                      videoId={v.id}
                      canEdit={true}
                      lang="fr"
                      onAccepted={(tag) => {
                        // Ajoute le tag au champ texte SANS écraser
                        // les tags existants tapés par Vincent.
                        setEditForm((prev) => {
                          const current = (prev.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
                          const norm = new Set(current.map((t) => t.toLowerCase()));
                          if (norm.has(tag.toLowerCase())) return prev;
                          const next = [...current, tag].join(", ");
                          return { ...prev, tags: next };
                        });
                      }}
                    />
                    {/* Vincent (2026-02) bug #525 (bis) — Multi-select Parcours
                        DANS le formulaire d'édition inline (alternative au
                        dropdown checklist toujours visible à droite).
                        Remplace l'ancien menu "Niveau 1 / 2 / 3 / 4" codé en
                        dur. Liste combinée : parcours d'intégration
                        (violet) + parcours d'apprentissage (émeraude).
                        Sauvegarde par diff via PATCH /api/pathways/{id}. */}
                    {allPathways.length > 0 && (
                      <div className="border border-border rounded p-2 bg-background max-h-40 overflow-y-auto" data-testid={`input-pathways-${v.id}`}>
                        <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">
                          Parcours associés ({(editForm.pathway_ids || new Set()).size})
                        </p>
                        {/* Tâche 1164 — Chercher plutôt que dérouler : à
                            trente parcours, la liste devient un couloir. */}
                        <input
                          value={pathwayQuery}
                          onChange={(e) => setPathwayQuery(e.target.value)}
                          placeholder={tr("Chercher un parcours…", "Search a pathway…")}
                          className="w-full mb-1 px-2 py-1 text-xs border border-border rounded"
                          data-testid={`pathway-search-${v.id}`}
                        />
                        {/* Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — 3 types */}
                        {pathwayQuery.trim()
                          && !allPathways.some((p) => matchesPathwayQuery(p, pathwayQuery)) && (
                          <p className="text-[11px] text-zinc-500 px-1 py-1" data-testid={`pathway-search-empty-${v.id}`}>
                            Aucun parcours ne contient « {pathwayQuery.trim()} ».
                          </p>
                        )}
                        {/* Tâche 1168 — « Utilisés récemment » en tête : les
                            fichiers arrivent par lots et se rattachent au
                            même parcours. */}
                        {["recent", "onboarding", "learning", "weekly"].map((ptype) => {
                          const list = ptype === "recent"
                            // L'ordre d'usage prime : trier par nom ferait
                            // perdre le « dernier utilisé ».
                            ? recentIds
                              .map((id) => allPathways.find((p) => p.id === id))
                              .filter(Boolean)
                              .filter((p) => matchesPathwayQuery(p, pathwayQuery))
                            : [...allPathways]
                              .filter((p) => (p.pathway_type === ptype))
                              .filter((p) => matchesPathwayQuery(p, pathwayQuery))
                              .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" }));
                          if (list.length === 0) return null;
                          const accent = ptype === "onboarding" ? "text-violet-600" : ptype === "weekly" ? "text-amber-600" : ptype === "recent" ? "text-zinc-600" : "text-teal-600";
                          const hoverBg = ptype === "onboarding" ? "hover:bg-violet-50" : ptype === "weekly" ? "hover:bg-amber-50" : ptype === "recent" ? "hover:bg-zinc-100" : "hover:bg-teal-50";
                          const labelText = ptype === "onboarding"
                            ? tr("Parcours d'intégration", "Onboarding pathways")
                            : ptype === "weekly"
                              ? tr("Formations hebdomadaires", "Weekly training")
                              : ptype === "recent"
                                ? tr("Utilisés récemment", "Recently used")
                                : tr("Parcours d'apprentissage", "Learning pathways");
                          // Tâche 1157 — Triangle pour replier chaque
                          // famille de parcours : trois listes dépliées d'un
                          // coup font défiler la modale sans fin.
                          const collapsed = !pathwayQuery.trim()
                            && collapsedPathwayTypes.has(ptype);
                          const chosen = list.filter((p) => (editForm.pathway_ids || new Set()).has(p.id)).length;
                          return (
                            <div key={ptype} className="mb-1 last:mb-0">
                              <button
                                type="button"
                                onClick={() => togglePathwayType(ptype)}
                                className={`w-full flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold ${accent} px-1 py-0.5 hover:bg-zinc-50 rounded`}
                                data-testid={`pathway-group-toggle-${v.id}-${ptype}`}
                              >
                                {collapsed ? <CaretRight size={10} /> : <CaretDown size={10} />}
                                <span>{labelText}</span>
                                <span className="text-zinc-400 normal-case">
                                  ({chosen}/{list.length})
                                </span>
                              </button>
                              {!collapsed && list.map((p) => {
                                const checked = (editForm.pathway_ids || new Set()).has(p.id);
                                return (
                                  <label
                                    key={`${ptype}-${p.id}`}
                                    className={`flex items-center gap-1.5 px-1 py-0.5 text-xs cursor-pointer rounded hover:bg-zinc-50 ${hoverBg}`}
                                    data-testid={`input-pathway-option-${v.id}-${ptype}-${p.id}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => {
                                        const next = new Set(editForm.pathway_ids || []);
                                        if (next.has(p.id)) next.delete(p.id);
                                        else {
                                          next.add(p.id);
                                          // Seul l'ajout compte : décocher
                                          // n'est pas « utiliser ».
                                          rememberPathway(p.id);
                                        }
                                        setEditForm({ ...editForm, pathway_ids: next });
                                      }}
                                      className={`h-3 w-3 ${ptype === "onboarding" ? "accent-violet-600" : "accent-emerald-600"}`}
                                    />
                                    <span className="truncate">{p.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {allTags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5 max-h-40 overflow-y-auto" data-testid={`tag-suggestions-${v.id}`}>
                        <span className="text-[10px] text-muted-foreground self-center mr-1 sticky top-0 bg-white z-10">
                          💡 Tags existants ({allTags.length}) :
                        </span>
                        {/* Vincent XLSX 2026-07-13 21h Row 46216 (Eric 2026-07-14 SPEC STRICK) —
                            Bug : seuls les 20 premiers tags s'affichaient (slice 0, 20).
                            Vincent exige que TOUS les tags soient visibles pour pouvoir
                            sélectionner "vente", "retraite", "pré-retraite", etc. La liste
                            passe donc sur allTags COMPLET, avec un container scrollable
                            (max-h-40 overflow-y-auto) pour éviter de casser la mise en page.
                            Le tri case-insensitive est déjà appliqué au niveau du dropdown. */}
                        {[...allTags]
                          .sort((a, b) => String(a).toLowerCase().localeCompare(String(b).toLowerCase(), "fr", { sensitivity: "base" }))
                          .map((tag) => {
                          const currentTags = editForm.tags.split(",").map(t => t.trim()).filter(Boolean);
                          const already = currentTags.includes(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                if (already) {
                                  const next = currentTags.filter(t => t !== tag).join(", ");
                                  setEditForm({ ...editForm, tags: next });
                                } else {
                                  const next = currentTags.length ? `${currentTags.join(", ")}, ${tag}` : tag;
                                  setEditForm({ ...editForm, tags: next });
                                }
                              }}
                              className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                                already
                                  ? "bg-[#002FA7] border-[#002FA7] text-white"
                                  : "bg-zinc-50 border-zinc-200 text-zinc-700 hover:bg-zinc-100"
                              }`}
                              title={already ? `Retirer le tag "${tag}"` : `Ajouter le tag "${tag}"`}
                              data-testid={`tag-chip-${v.id}-${tag}`}
                            >
                              {already ? "✓ " : "+ "}{tag}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      💡 <strong>{tr("Astuce arborescence :", "Tree tip:")}</strong> {tr("séparez les niveaux par", "separate the levels with")} <code className="bg-muted px-1 py-0.5 rounded">/</code> {tr("pour créer une hiérarchie. Exemple :", "to build a hierarchy. Example:")} <code className="bg-muted px-1 py-0.5 rounded">Investissement / 1. Connaissances / 1. Introduction</code>
                    </p>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={cancelEdit} data-testid={`btn-cancel-edit-${v.id}`}>
                        <X size={14} className="mr-1" /> Annuler
                      </Button>
                      <Button size="sm" onClick={() => saveEdit(v.id)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`btn-save-edit-${v.id}`}>
                        <Check size={14} className="mr-1" /> Sauver
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <FileVideo size={18} weight="duotone" className="text-[#002FA7] flex-shrink-0" />
                    {/* Bug 545 (Eric 2026-07-24) — Bouton favori.
                        Vincent B588 (Eric 2026-07-28 SPEC STRICK) — Migration
                        étoile → cœur pour cohérence avec l'iconographie
                        favoris de la Base de connaissances. Le cœur rouge
                        (favori actif) reste safe daltonien : la sémantique
                        est portée par la variante `fill` vs `regular`, pas
                        par la couleur seule.
                        Vincent B604 (Eric 2026-07-29 SPEC STRICK) — Le ♥
                        s'affiche pour vidéos ET textes (documents stockés
                        dans la même collection videos avec is_text_doc). */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(v); }}
                      className={`flex-shrink-0 p-1 rounded transition-colors ${
                        favVideoIds.has(v.id)
                          ? "text-pink-500 hover:text-pink-600"
                          : "text-zinc-300 hover:text-pink-400"
                      }`}
                      data-testid={`fav-btn-${v.id}`}
                      title={favVideoIds.has(v.id)
                        ? tr("Retirer des favoris", "Remove from favourites")
                        : tr("Ajouter aux favoris", "Add to favourites")}
                      aria-label={favVideoIds.has(v.id)
                        ? tr("Retirer des favoris", "Remove from favourites")
                        : tr("Ajouter aux favoris", "Add to favourites")}
                    >
                      <Heart size={16} weight={favVideoIds.has(v.id) ? "fill" : "regular"} />
                    </button>
                    <div className="flex-1 min-w-0">
                      {/* Tâche 1365 (Eric 2026-06-30) — Le « 100 % » de
                          visionnage occupait la ligne SUIVANTE, loin du
                          fichier auquel il se rapporte. Il vit désormais à
                          DROITE du nom, sur la même ligne : le nom garde sa
                          troncature (`min-w-0` + `truncate`) et la pastille
                          ne se comprime pas (`flex-shrink-0`) — sans ces deux
                          règles, un titre long pousserait la pastille hors de
                          l'écran, c'est-à-dire la ferait disparaître. */}
                      <div className="flex items-center gap-2 min-w-0">
                        <button
                          onClick={() => navigate(`/dashboard/video/${v.id}`)}
                          className="font-medium text-sm hover:underline truncate block text-left min-w-0"
                          data-testid={`link-video-${v.id}`}
                        >
                          <HighlightedText text={v.title || "Sans titre"} query={search} testid={`title-hl-${v.id}`} />
                        </button>
                        {/* Tâche 1412 (Eric 2026-10-01) — Cette enveloppe
                            était `flex-shrink-0` (acquis 1365 : un titre long
                            ne doit pas pousser le « 100 % » hors de l'écran).
                            Mais le crayon et la corbeille vivaient DEDANS :
                            incompressibles, ils sortaient de l'écran sur les
                            titres longs. Mesuré à 390 px, une ligne sur deux
                            cents perdait ses deux actions.
                            L'enveloppe peut donc se rogner, et les actions
                            sont devenues ses SŒURS, incompressibles. Une
                            pastille rognée de quelques pixels (« 45 s (2% »)
                            reste lisible ; une action hors écran est perdue.
                            Mesuré après correction : 200/200 lignes correctes
                            à 1920 px ET à 390 px, aucun débordement. */}
                        <span
                          className="inline-flex flex-nowrap items-center gap-1.5 min-w-0 overflow-hidden"
                          data-testid={`video-title-badges-${v.id}`}
                        >
                        {!v.is_text_doc && (() => {
                            const wp = watchProgress[v.id] || {};
                            const secs = wp.seconds_watched || 0;
                            const pct = wp.watch_pct || 0;
                            const okWatched = !!wp.watched_ok;
                            const fmtDur = (s) => {
                              // Vincent B645 (Eric 2026-08-01 SPEC STRICK PROD) —
                              // Format : arrondi entier (2 min 40.8 s → 2 min 41 s),
                              // ne pas afficher les heures si 0.
                              const total = Math.round(Number(s) || 0);
                              if (!total) return "0 s";
                              const h = Math.floor(total / 3600);
                              const m = Math.floor((total % 3600) / 60);
                              const rs = total % 60;
                              const parts = [];
                              if (h > 0) parts.push(`${h} h`);
                              if (m > 0) parts.push(`${m} min`);
                              // On affiche les secondes uniquement si non-nulles
                              // OU si c'est la seule unité présente.
                              if (rs > 0 || parts.length === 0) parts.push(`${rs} s`);
                              return parts.join(" ");
                            };
                            return (
                              <span
                                className={`text-[10px] font-medium px-1.5 py-0.5 rounded border whitespace-nowrap inline-flex items-center gap-1 ${
                                  okWatched
                                    ? "border-teal-300 bg-teal-50 text-teal-700"
                                    : "border-violet-200 bg-violet-50 text-violet-700"
                                }`}
                                data-testid={`video-watch-badge-${v.id}`}
                                title={`${secs}s / ${wp.duration || "?"}s`}
                              >
                                {okWatched && <CheckCircle size={11} weight="fill" data-testid={`video-watch-check-${v.id}`} />}
                                {fmtDur(secs)} ({pct}%)
                              </span>
                            );
                          })()}
                        {(() => {
                        const cov = coverageMap[v.id];
                        if (!cov || cov.coverage_ratio == null) return null;
                        const pct = Math.round(cov.coverage_ratio * 100);
                        const isRed = cov.coverage_ratio < 0.6;
                        const isAmber = cov.coverage_ratio >= 0.6 && cov.coverage_alert;
                        const isGreen = !isRed && !isAmber;
                        const color = isRed
                          ? "bg-red-50 text-red-800 border-red-200"
                          : isAmber
                            ? "bg-amber-50 text-amber-800 border-amber-200"
                            : "bg-emerald-50 text-emerald-800 border-emerald-200";
                        const icon = isRed ? "🚨" : isAmber ? "⚠️" : "✓";
                        const gapMin = cov.gap_min != null ? ` · ${Math.round(cov.gap_min)} min manquantes` : "";
                        const title = isGreen
                          ? `Couverture complète (${pct} %) — aucun contenu perdu à la fusion`
                          : `Couverture faible (${pct} %)${gapMin} — Cliquez sur le titre pour corriger`;
                        return (
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${color}`}
                            data-testid={`coverage-badge-${v.id}`}
                            data-coverage-alert={cov.coverage_alert ? "true" : "false"}
                            title={title}
                            aria-label={title}
                          >
                            <span aria-hidden="true">{icon}</span>
                            <span data-testid={`coverage-badge-pct-${v.id}`}>{pct} %</span>
                          </span>
                        );
                      })()}
                        {/* Bogue 1365 (Eric 2026-09-30, 2e passe) — « Idem
                            pour la mention Vérifiée » : elle occupait encore
                            la ligne SUIVANTE, séparée du fichier auquel elle
                            se rapporte. Les deux marques vertes remontent
                            donc sur la ligne du nom, à droite de la pastille
                            de visionnage. Elles restent ENSEMBLE : n'en
                            remonter qu'une laisserait « Dictionnaire » seule
                            en dessous, ce qui donnerait à voir un défaut
                            d'affichage là où il n'y en a pas.
                            Tâche 1336 — Elles n'apparaissent que si c'est
                            fait, et une nouvelle fusion les efface (1331). */}
                        {(v.web_verified || v.dictionary_generated) && (
                          <span className="inline-flex items-center gap-1" data-testid={`explorer-green-marks-${v.id}`}>
                            {v.web_verified && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-800 border-emerald-200 whitespace-nowrap"
                                data-testid={`explorer-web-verified-${v.id}`}
                                title={tr(
                                  "Synthèse vérifiée sur le web. Une nouvelle fusion efface cette marque.",
                                  "Synthesis verified on the web. A new fusion clears this mark.",
                                )}
                              >
                                <CheckCircle size={11} weight="fill" />
                                {tr("Vérifiée", "Verified")}
                              </span>
                            )}
                            {v.dictionary_generated && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-800 border-emerald-200 whitespace-nowrap"
                                data-testid={`explorer-dictionary-generated-${v.id}`}
                                title={tr(
                                  "Dictionnaire généré à partir de la synthèse. Une nouvelle fusion efface cette marque.",
                                  "Dictionary generated from the synthesis. A new fusion clears this mark.",
                                )}
                              >
                                <CheckCircle size={11} weight="fill" />
                                {tr("Dictionnaire", "Dictionary")}
                              </span>
                            )}
                          </span>
                        )}
                        {/* Tâche 1412 (Eric 2026-09-30) — « Les icônes crayon
                            et corbeille sont à placer à droite du nom de
                            fichier puis des étiquettes. » Elles suivent donc
                            les pastilles, sur la ligne du nom, au lieu de
                            vivre sur la ligne des informations (1173). Le
                            clic ne doit pas ouvrir la fiche : l'enveloppe
                            arrête la propagation. */}
                        </span>
                        {/* Tâche 1412 (Eric 2026-10-01) — Les actions sont
                            SŒURS des pastilles, et non dedans. Dans
                            l'enveloppe, elles héritaient de son rognage : à
                            390 px elles sortaient de l'écran, c'est-à-dire
                            qu'elles DISPARAISSAIENT — pire que de passer à la
                            ligne. Ici, l'ordre de sacrifice est explicite :
                            le nom se tronque d'abord, les pastilles se
                            rognent ensuite, et le crayon et la corbeille
                            restent toujours visibles.
                            Pas de `ml-auto` : il les collait au bord droit de
                            la fenêtre, à plus de 800 px du nom sur un grand
                            écran. Eric a demandé « à droite du NOM puis des
                            étiquettes » — donc à côté, pas à l'autre bout. */}
                        {isAdmin && (
                          <span
                            className="inline-flex items-center gap-1 align-middle flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`explorer-row-actions-${v.id}`}
                          >
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); startEdit(v); }}
                              className="p-1 rounded hover:bg-zinc-100 text-zinc-600"
                              title={tr("Modifier les informations du fichier", "Edit the file information")}
                              data-testid={`btn-edit-${v.id}`}
                            >
                              <PencilSimple size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(v); }}
                              className="p-1 rounded text-red-600 hover:bg-red-50 hover:text-red-700"
                              title={tr("Supprimer le fichier et toutes ses connaissances", "Delete the file and all its knowledge")}
                              data-testid={`btn-delete-video-${v.id}`}
                            >
                              <Trash size={14} />
                            </button>
                          </span>
                        )}
                      </div>
                      {/* Tâche 1304 (Eric 2026-06-30) — Quand le fichier est
                          trouvé par son IDENTIFIANT, on affiche cet
                          identifiant : sans lui, rien ne confirme qu'on est
                          tombé sur le bon fichier. */}
                      {isAdmin && matchesId(v.id, search) && (
                        <p
                          className="text-[10px] font-mono text-[#002FA7] mt-0.5 break-all"
                          data-testid={`explorer-matched-id-${v.id}`}
                          title={tr("Trouvé par son identifiant", "Found by its identifier")}
                        >
                          {v.id}
                        </p>
                      )}
                      {/* Vincent B894 (Eric 2026-02-14 SPEC STRICK PROD) —
                          Badge inline « couverture % » visible dans la liste
                          Explorateur pour repérer d'un coup d'œil les vidéos
                          ayant perdu du contenu au merge. Code couleur :
                          rouge < 60 %, ambre 60-89 % (alerte), vert ≥ 90 %.
                          N'apparaît QUE si un ratio est disponible (source
                          `/api/videos/coverage-report`). Cliquable → ouvre
                          la vidéo (idem lien titre) pour aller corriger. */}
                      
                      {/* Tâche 1336 — Les deux marques vertes ont rejoint la
                          ligne du NOM de fichier (bogue 1365, 2e passe) :
                          voir le bloc `explorer-green-marks-*` plus haut. */}
                      {/* Vincent B629 (Eric 2026-07-30 SPEC STRICK PROD) —
                          Thème de formation affiché à gauche, SOUS le nom
                          du fichier, en READ-ONLY quand on n'est pas en
                          mode édition. Édition possible uniquement via le
                          crayon (mode édition ci-dessus), fidèle à la
                          demande de Vincent. Libellé FR-only via
                          `trainingThemeLabel`. */}
                      {/* Eric 2026-07-30 (B609 follow-up, enhancement approuvé) —
                          Aperçu inline (2-3 premières lignes non-vides) du
                          contenu textuel pour permettre au learner de repérer
                          le bon document d'un coup d'œil, sans devoir l'ouvrir.
                          `content_preview` est calculé côté backend
                          (routers/videos.py `list_videos`) pour éviter d'envoyer
                          les 2-25 Mo bruts par doc dans la liste.
                          Eric 2026-07-30 (2e itération) — Les mots recherchés
                          sont surlignés (bg amber) pour une découverte 3-4×
                          plus rapide dans la Textes. */}
                      {v.is_text_doc && v.content_preview && (
                        <p
                          className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2 leading-snug"
                          data-testid={`text-doc-preview-${v.id}`}
                          title={v.content_preview}
                        >
                          <HighlightedText text={v.content_preview} query={search} testid={`preview-hl-${v.id}`} />
                        </p>
                      )}
                      {/* Vincent B615 — VOIR PLUS BAS : le Tag durée est
                          désormais INLINE dans la ligne des tags (OK +
                          intro + recrutement + …) plutôt que sur une ligne
                          dédiée, conformément à la précision de Vincent
                          (2026-07-30 SPEC STRICK PROD, 3e itération) :
                          « à la 2e ligne sous son titre (là où c'est déjà
                          indiqué) ». */}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {/* Tâche 1173 (Eric 2026-06-25) — Le thème de formation
                            ouvre la ligne d'informations (il occupait une ligne
                            à lui seul auparavant). Lecture seule ici : édition
                            via le crayon. */}
                        <span
                          className="text-[10px] text-zinc-500 whitespace-nowrap"
                          data-testid={`training-theme-label-${v.id}`}
                          title={tr("Thème de formation (éditable via ✎)", "Training theme (editable via ✎)")}
                        >
                          <span className="uppercase tracking-wider font-semibold text-zinc-400 mr-1">{tr("Thème :", "Theme:")}</span>
                          <span className="font-medium text-zinc-700">{trainingThemeLabel(v.training_theme, lang)}</span>
                        </span>
                        {/* Tâche 1472 (Eric 2026-06) — Le DOSSIER sur la ligne
                            d'informations, après le thème : il dit où le
                            fichier est rangé, et il n'apparaissait nulle part
                            en lecture (seulement en mode édition). */}
                        {v.module_name && (
                          <span
                            className="text-[10px] text-zinc-500 whitespace-nowrap max-w-[260px] truncate"
                            data-testid={`explorer-folder-label-${v.id}`}
                            title={`${tr("Dossier", "Folder")} : ${v.module_name}`}
                          >
                            <span className="uppercase tracking-wider font-semibold text-zinc-400 mr-1">{tr("Dossier :", "Folder:")}</span>
                            <span className="font-medium text-zinc-700">{v.module_name}</span>
                          </span>
                        )}
                        {/* Tâche 1172 + 1173 + 1173b (Eric 2026-06-25) — bouton
                            Tags (fenêtre contextuelle), catégories, puis parcours.
                            1173b : sur ordinateur ce bloc s'insère DANS la ligne
                            d'informations, juste après la durée visionnée / le quiz
                            et juste avant « Fusion en attente » + le crayon. Sur
                            mobile il garde sa propre ligne (basis-full + order-last
                            le renvoient en fin de rangée, sous les boutons). */}
                        <div className="flex flex-wrap items-center gap-2 basis-full min-w-0 order-last mt-1 lg:basis-auto lg:order-none lg:mt-0">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTagsVid(openTagsVid === v.id ? null : v.id);
                        }}
                        className="text-[11px] px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 font-semibold inline-flex items-center gap-1"
                        title={tr("Voir les tags de ce fichier", "View the tags of this file")}
                        data-testid={`btn-tags-${v.id}`}
                      >
                        <Tag size={11} />
                        Tags ({(v.tags || []).length})
                      </button>
                      {openTagsVid === v.id && (
                        <div
                          className="absolute z-40 left-0 top-full mt-1 w-64 max-h-64 overflow-auto rounded-lg border border-zinc-200 bg-white p-2 shadow-xl"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`tags-popover-${v.id}`}
                        >
                          {(v.tags || []).length === 0 ? (
                            <p className="text-[11px] text-zinc-500">{tr("Aucun tag sur ce fichier.", "No tag on this file.")}</p>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(v.tags || []).slice().sort((a, b) => String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' })).map((tag) => (
                                <span
                                  key={tag}
                                  className="text-[10px] bg-[#002FA7]/10 text-[#002FA7] px-1.5 py-0.5 rounded font-semibold"
                                  data-testid={`tags-popover-item-${v.id}-${tag}`}
                                >
                                  <Tag size={9} className="inline mr-0.5" />{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {isAdmin && categories.length > 0 && (
                      <select
                        value={v.category_id || ""}
                        disabled={assigningCatVid === v.id}
                        onChange={async (e) => {
                          const newCatId = e.target.value || null;
                          setAssigningCatVid(v.id);
                          try {
                            await axios.put(
                              `${API}/api/videos/${v.id}/category`,
                              { category_id: newCatId },
                              { withCredentials: true }
                            );
                            setVideos((prev) => prev.map((x) => x.id === v.id ? { ...x, category_id: newCatId } : x));
                            const catName = categories.find((c) => c.id === newCatId)?.name;
                            toast.success(catName
                              ? tr(`Catégorie : ${catName}`, `Category: ${catName}`)
                              : tr("Catégorie retirée", "Category removed"));
                          } catch (err) {
                            toast.error(explainError(err, tr("Échec d'attribution de la catégorie", "Could not assign the category")));
                          } finally {
                            setAssigningCatVid(null);
                          }
                        }}
                        className="text-[10px] px-1.5 py-1 rounded border border-border bg-background hover:bg-muted disabled:opacity-50 max-w-[9rem] lg:max-w-none"
                        data-testid={`category-select-${v.id}`}
                        title={tr("Attribuer une catégorie de contenu", "Assign a content category")}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">{tr("— Catégorie —", "— Category —")}</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>{`${c.icon || ""} ${c.name}`}</option>
                        ))}
                      </select>
                    )}
                    {/* Vincent #163 — Dropdown checklist par vidéo : choisir dans quels parcours apparaît cette vidéo */}
                    {isAdmin && (
                      <div className="relative" data-testid={`pathway-dropdown-wrap-${v.id}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Vincent #287 (2026-06-11) — le menu était coupé par
                            // l'overflow-hidden des cadres de groupe. On mémorise la
                            // position du bouton et on rend le menu en position FIXED
                            // par-dessus toute l'interface (z-index élevé), il peut
                            // déborder sous le cadre sans être rogné.
                            if (openPathwayDropdown === v.id) {
                              setOpenPathwayDropdown(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setPathwayDropdownPos({
                                top: rect.bottom + 4,
                                right: Math.max(8, window.innerWidth - rect.right),
                              });
                              setOpenPathwayDropdown(v.id);
                            }
                          }}
                          className={`text-[10px] px-1.5 py-1 rounded border border-border bg-background hover:bg-muted inline-flex items-center gap-1 ${(allPathways.filter(p => (p.video_ids || []).includes(v.id)).length > 0) ? "text-[#002FA7] font-medium" : "text-zinc-500"}`}
                          data-testid={`pathway-dropdown-btn-${v.id}`}
                          title={tr("Inclure cette vidéo dans des parcours", "Include this video in pathways")}
                        >
                          📚 {tr("Parcours", "Pathways")}
                          {(() => {
                            const count = allPathways.filter(p => (p.video_ids || []).includes(v.id)).length;
                            return count > 0 ? <span className="bg-[#002FA7] text-white px-1 rounded text-[9px]">{count}</span> : null;
                          })()}
                        </button>
                        {openPathwayDropdown === v.id && (
                          <>
                            {/* backdrop pour fermer en cliquant ailleurs */}
                            <div className="fixed inset-0 z-[80]" onClick={() => setOpenPathwayDropdown(null)} />
                            <div
                              className="fixed w-72 bg-white border border-zinc-200 rounded-lg shadow-xl z-[90] max-h-80 overflow-y-auto"
                              style={{ top: pathwayDropdownPos.top, right: pathwayDropdownPos.right }}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`pathway-dropdown-${v.id}`}
                            >
                              <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50 text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">
                                {tr("Inclure dans les parcours", "Include in pathways")}
                              </div>
                              {allPathways.length === 0 ? (
                                <div className="px-3 py-3 text-xs text-zinc-400 italic">
                                  Aucun parcours créé. Allez dans Apprentissage &gt; Parcours d&apos;intégration, Parcours d&apos;apprentissage ou Formations hebdomadaires pour en créer.
                                </div>
                              ) : (
                                <ul className="divide-y divide-zinc-100">
                                  {/* Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — Intégration → apprentissage → hebdomadaire */}
                                  {["onboarding", "learning", "weekly"].map(ptype => {
                                    const subset = allPathways.filter(p => p.pathway_type === ptype);
                                    if (subset.length === 0) return null;
                                    const labelText = ptype === "onboarding"
                                      ? tr("Parcours d'intégration", "Onboarding pathways")
                                      : ptype === "weekly"
                                        ? "Formations hebdomadaires"
                                        : "Parcours d'apprentissage";
                                    return (
                                      <li key={ptype} className="py-1">
                                        <div className="px-3 py-1 text-[9px] uppercase text-zinc-400 font-bold tracking-wider">
                                          {labelText}
                                        </div>
                                        {subset.map(p => {
                                          const isMember = (p.video_ids || []).includes(v.id);
                                          return (
                                            <label
                                              key={p.id}
                                              className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-zinc-50 cursor-pointer"
                                              data-testid={`pathway-toggle-${v.id}-${p.id}`}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={isMember}
                                                onChange={() => togglePathwayMembership(v.id, p)}
                                                className="w-3.5 h-3.5 accent-[#002FA7]"
                                              />
                                              <span className="truncate flex-1">{p.name}</span>
                                              {!p.is_published && (
                                                <span className="text-[9px] bg-zinc-200 text-zinc-600 px-1 rounded">brouillon</span>
                                              )}
                                            </label>
                                          );
                                        })}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    </div>
                        {/* Vincent (2026-02) #527 — Badges d'état enrichis :
                            codec non-standard (optimisation FFmpeg échouée),
                            pause fusion / extraction (attente utilisateur). */}
                        {v.optimization_failed && !v.optimized && (
                          <span
                            className={`text-[10px] ${lang === "fr" ? "" : "uppercase"} tracking-wider font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 inline-flex items-center gap-1`}
                            data-testid={`badge-codec-nonstandard-${v.id}`}
                            title={tr("Codec non compatible navigateur (HEVC/AV1/yuv422p) — écran noir dans Chrome/Firefox. Cliquez sur « Ré-optimiser » pour ré-encoder en H.264/yuv420p.", "Codec not browser-compatible (HEVC/AV1/yuv422p) — black screen in Chrome/Firefox. Click “Re-optimize” to re-encode to H.264/yuv420p.")}
                          >
                            <FilmSlate size={10} /> {tr("Codec non-standard", "Non-standard codec")}
                          </span>
                        )}
                        {/* Tâche 1472 (Eric 2026-06) — Les informations de
                            PIPELINE ferment la ligne : thème, tags, dossier
                            et parcours décrivent le fichier, le pipeline
                            décrit son traitement. Ordre demandé par Eric. */}
                        {/* Vincent B561 (Eric 2026-07-25) — Boutons pause/relance
                            à GAUCHE du bandeau pipeline, accessibles directement
                            depuis l'Explorateur de fichiers (pas besoin d'ouvrir
                            « File d'attente »). Couleur = état actuel :
                            - bleu = en cours → propose Pause
                            - ambre = en pause → propose Relance
                            - vert / rouge = pas de bouton (fini / annulé). */}
                        {isAdmin && (
                          (v.pipeline_status && !["complete", "cancelled"].includes(v.pipeline_status)) ? (
                            v.pipeline_status.startsWith("paused") || v.pipeline_status === "failed" ? (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const { data } = await axios.post(`${API}/api/videos/${v.id}/queue/resume`, {}, { withCredentials: true });
                                    toast.success(`Pipeline relancé (depuis ${data.from_status})`);
                                    setVideos((prev) => prev.map((x) => x.id === v.id ? { ...x, pipeline_status: "pending" } : x));
                                  } catch (err) {
                                    toast.error(explainError(err, tr("Impossible de relancer le pipeline", "Could not restart the pipeline")));
                                  }
                                }}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-300 font-bold inline-flex items-center gap-1"
                                data-testid={`pipeline-resume-btn-${v.id}`}
                                title={tr(`Relancer le pipeline (depuis ${v.pipeline_status})`, `Run the pipeline again (from ${v.pipeline_status})`)}
                              >
                                <Play size={10} weight="fill" /> {tr("Relancer", "Run again")}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await axios.post(`${API}/api/videos/${v.id}/queue/pause`, {}, { withCredentials: true });
                                    toast.success("Pipeline mis en pause");
                                    setVideos((prev) => prev.map((x) => x.id === v.id ? { ...x, pipeline_status: "paused" } : x));
                                  } catch (err) {
                                    toast.error(explainError(err, tr("Impossible de mettre en pause", "Could not pause")));
                                  }
                                }}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-950 dark:text-blue-300 font-bold inline-flex items-center gap-1"
                                data-testid={`pipeline-pause-btn-${v.id}`}
                                title={tr(
                                  `Mettre le pipeline en pause (actuellement : ${v.pipeline_status})`,
                                  `Pause the pipeline (currently: ${v.pipeline_status})`,
                                )}
                              >
                                <PauseCircle size={10} weight="fill" /> Pause
                              </button>
                            )
                          ) : null
                        )}
                        <span className={`text-[10px] ${lang === "fr" ? "" : "uppercase"} tracking-wider font-bold px-1.5 py-0.5 rounded ${
                          v.pipeline_partiel ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300" :
                          v.pipeline_status === "complete" ? "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300" :
                          v.pipeline_status === "failed" ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" :
                          "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                        }`}
                        data-testid={`video-status-badge-${v.id}`}
                        /* Tâche 1487 — Au survol, CE QUI MANQUE. Un badge
                           « partiellement complété » sans la liste obligerait
                           à ouvrir la vidéo pour deviner. */
                        title={v.pipeline_partiel
                          ? tr(`Pipeline arrêté par ${v.pipeline_conclu_par || "un utilisateur"} — il manque : ${(v.pipeline_manquants || []).join(", ") || "tout"}`,
                               `Pipeline stopped by ${v.pipeline_conclu_par || "a user"} — missing: ${(v.pipeline_manquants || []).join(", ") || "everything"}`)
                          : undefined}
                        >
                          {v.pipeline_partiel
                            ? tr("PARTIEL", "PARTIAL")
                            : v.pipeline_status === "complete"
                            ? "OK"
                            : v.pipeline_status === "failed"
                            ? tr("ÉCHEC", "FAILED")
                            // Vincent (2026-02) #520 — Affiche l'étape EXACTE
                            // en cours (ex : "Transcription (Whisper)") au
                            // lieu du générique "EN COURS", en s'appuyant sur
                            // le map PIPELINE_STAGES.
                            : (PIPELINE_STAGES[v.pipeline_stage] ? pipelineStageLabel(v.pipeline_stage, lang) : tr("EN COURS", "RUNNING"))}
                        </span>
                        {/* Tâche 1476 (Eric) — « partout où une vidéo apparaît
                            en attente ». L'Explorateur porte la même pastille
                            d'estimation que la File d'attente, par le même
                            composant. */}
                        <EtaAttente video={v} compact />
                        {/* Tâche 1491 (Eric) — Conclure le pipeline sans
                            passer par la File d'attente. Masqué quand il n'y
                            a plus rien à conclure (déjà terminé ou déjà
                            marqué partiel) : un bouton sans effet est un
                            bouton qui trompe. */}
                        {isAdmin && !v.is_text_doc && !v.pipeline_partiel
                          && v.pipeline_status !== "complete" && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); arreterPipelineTermine(v); }}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-zinc-300 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 whitespace-nowrap"
                            title={tr(
                              "Arrêter le pipeline et l'afficher comme terminé si des connaissances existent déjà",
                              "Stop the pipeline and show it as complete if knowledge already exists",
                            )}
                            data-testid={`stop-pipeline-done-${v.id}`}
                          >
                            {tr("Arrêter le pipeline (terminé)", "Stop pipeline (done)")}
                          </button>
                        )}
                        {/* Vincent B740 v3 (Eric 2026-02-14) — Badge « ★ Canonique »
                            à droite du badge de statut. Affiche la source
                            canonique choisie par l'admin dans TranscriptPanel
                            (Whisper / Fireflies / Fusionnée). Se masque tant
                            que la vidéo n'est pas prête (pipeline en cours). */}
                        {v.pipeline_status === "complete" && v.canonical_transcript_source && (() => {
                          const srcLabel = libelleSource(v.canonical_transcript_source, lang);
                          return (
                            <span
                              className="text-[11px] px-2 py-1 rounded border font-semibold whitespace-nowrap inline-flex items-center gap-1 border-teal-400 bg-teal-50 text-teal-800"
                              data-testid={`video-canonical-badge-${v.id}`}
                              data-canonical-source={v.canonical_transcript_source}
                              title={lang === "fr"
                                ? `Source canonique de cette vidéo : ${srcLabel}. Utilisée par Q&R / Scripts / Procédures / RAG.`
                                : `Canonical source for this video: ${srcLabel}. Used by Q&A / Scripts / Procedures / RAG.`}
                            >
                              <span aria-hidden="true">★</span>
                              {tr("canonique :", "Canonical:")} {srcLabel}
                            </span>
                          );
                        })()}
                        {/* Vincent B615 (Eric 2026-07-30 SPEC STRICK — 3e itération PROD) —
                            Tag durée de visionnage INLINE dans la ligne des tags,
                            immédiatement après le badge de statut « OK ». Le Tag
                            respecte STRICTEMENT la spec du Tag Quiz
                            (LearnerPathwaysView.js:374) : text-[11px],
                            px-2 py-1, rounded, border, font-semibold,
                            whitespace-nowrap, inline-flex items-center gap-1.
                            Couleur teal (avec ✓ vert) quand la vidéo est
                            visionnée au-delà du seuil, violette sinon.
                            Affiché pour CHAQUE vidéo (0 s / 0 % par défaut).
                            Masqué uniquement pour les documents texte. */}
                        {v.pipeline_status === "paused_pending_fusion" && (
                          <span
                            className={`text-[10px] ${lang === "fr" ? "" : "uppercase"} tracking-wider font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 inline-flex items-center gap-1`}
                            data-testid={`badge-paused-fusion-${v.id}`}
                            title={tr("Whisper terminé — en attente de révision de la fusion Fireflies.", "Whisper done — waiting for the Fireflies merge review.")}
                          >
                            <PauseCircle size={10} /> {tr("Fusion en attente", "Merge pending")}
                          </span>
                        )}
                        {v.pipeline_status === "paused_pending_extraction" && (
                          <span
                            className={`text-[10px] ${lang === "fr" ? "" : "uppercase"} tracking-wider font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 inline-flex items-center gap-1`}
                            data-testid={`badge-paused-extraction-${v.id}`}
                            title={tr("Transcription terminée — en attente du lancement de l'extraction des connaissances.", "Transcript done — waiting for the knowledge extraction to be started.")}
                          >
                            <PauseCircle size={10} /> {tr("Extraction en attente", "Extraction pending")}
                          </span>
                        )}
                        {/* Vincent B585 récurrence (Eric B695 2026-02-XX) —
                            2 boutons d'action côte-à-côte quand la vidéo est
                            en pause pipeline. Réservés aux éditeurs. Éviter
                            le double clic (stopPropagation). */}
                        {/* B1090 (Eric 2026-09-05) — Les 2 boutons sont
                            désormais visibles EN TOUT TEMPS, y compris quand
                            la vidéo est entièrement traitée : Vincent doit
                            pouvoir refaire la transcription ou relancer les
                            connaissances sans attendre une pause. Désactivés
                            pendant un traitement en cours (deux pipelines
                            simultanés sur la même vidéo se marcheraient
                            dessus). */}
                        {canEditContent(user) && (
                          <span
                            className="inline-flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Tâche 1173 (Eric 2026-06-25) — Les boutons « Recommencer
                                (transcription) » et « Continuer (connaissances) » sont
                                retirés de l'explorateur : ils encombraient la ligne du
                                fichier. Ils restent disponibles sur la fiche du
                                fichier. */}
                            {/* Tâche 1493 (Eric 2026-06) — ⚠️ Le menu revient, mais
                                UN SEUL déclencheur au lieu de deux boutons : c'est
                                ici qu'Eric suit plusieurs vidéos, donc ici qu'il veut
                                reprendre une étape sans ouvrir la fiche. Son contenu
                                n'est chargé qu'au clic (voir MenuContinuerPipeline). */}
                            {v.pipeline_status !== "processing" && v.pipeline_status !== "pending" && (
                              <MenuContinuerPipeline
                                api={API}
                                video={v}
                                sizeClass="px-1.5 py-0.5 text-[10px]"
                                triggerClass="bg-teal-700 hover:bg-teal-600 border border-teal-600 text-white"
                                onFait={loadAll}
                              />
                            )}
                            {/* B1116 (Eric 2026-09-08) — Arrêt forcé aussi
                                dans la liste : masqué sur une vidéo terminée
                                ou déjà arrêtée, il n'y a plus rien à couper. */}
                            {v.pipeline_status !== "complete" && v.pipeline_status !== "cancelled" && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  doForceStopInList(v.id);
                                }}
                                disabled={resumeBusyVid === v.id}
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-red-300 bg-white hover:bg-red-50 text-red-700 disabled:opacity-50 inline-flex items-center gap-1"
                                data-testid={`btn-force-stop-${v.id}`}
                                title={tr("Forcer l'arrêt du pipeline : l'étape en cours se termine, aucune suivante ne démarre (plus aucun crédit consommé)", "Force-stop the pipeline: the current step finishes, no further step starts (no more credits consumed)")}
                              >
                                <Prohibit size={10} weight="bold" />
                                {resumeBusyVid === v.id ? "…" : tr("Forcer l'arrêt", "Force stop")}
                              </button>
                            )}
                          </span>
                        )}
                        {/* Vincent (2026-02) #527 — « Ré-optimiser » (codec
                            non-standard : HEVC/AV1/yuv422p → écran noir).
                            Tâche 1173 : déplacé ici, sur la ligne des infos. */}
                        {isAdmin && v.optimization_failed && !v.optimized && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); reoptimizeVideo(v.id); }}
                            disabled={reoptimizingVid === v.id}
                            className="p-1 rounded text-rose-600 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
                            data-testid={`btn-reoptimize-${v.id}`}
                            title={tr("Ré-optimiser (FFmpeg H.264/yuv420p) pour rendre la vidéo lisible dans les navigateurs", "Re-optimize (FFmpeg H.264/yuv420p) to make the video playable in browsers")}
                          >
                            {reoptimizingVid === v.id
                              ? <SpinnerGap size={14} className="animate-spin" />
                              : <FilmSlate size={14} />}
                          </button>
                        )}
                        {/* Vincent (2026-02) #527 — « Relancer » (vidéo échouée
                            ou bloquée en pause). Tâche 1173 : déplacé ici. */}
                        {isAdmin && (
                          v.pipeline_status === "failed"
                          || v.pipeline_status === "paused_pending_fusion"
                          || v.pipeline_status === "paused_pending_extraction"
                          || v.pipeline_status === "paused_awaiting_user"
                        ) && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); reprocessVideo(v.id); }}
                            disabled={reprocessingVid === v.id}
                            className="p-1 rounded text-amber-700 hover:bg-amber-50 hover:text-amber-800 disabled:opacity-50"
                            data-testid={`btn-reprocess-${v.id}`}
                            title={tr("Relancer le pipeline (transcription + diarisation + extraction) depuis le début", "Run the pipeline again (transcription + diarization + extraction) from the start")}
                          >
                            {reprocessingVid === v.id
                              ? <SpinnerGap size={14} className="animate-spin" />
                              : <ArrowClockwise size={14} />}
                          </button>
                        )}
                        {/* Tâche 1412 (Eric 2026-09-30) — Le crayon et la
                            corbeille ont quitté cette ligne : ils sont
                            maintenant à droite du nom du fichier, après les
                            pastilles (durée, %, Vérifiée). */}
                      </div>
                    </div>
                    {/* Tâche 1173 (Eric 2026-06-25) — « Ré-optimiser » et
                        « Relancer » ont migré dans la 1re ligne d'informations
                        (avec le crayon et la corbeille) pour que la fiche tienne
                        en exactement deux lignes sous le titre. */}
                  </div>
                )}
                </div>
              </div>
            );

            /* Tâche 1435 (Eric 2026-10-02) — Les feuilles d'un même classeur
               Excel sont réunies sous un dossier PLIABLE, à la place de la
               première d'entre elles. Le regroupement est fait par
               `import_group_id` et NON par voisinage dans la liste : selon le
               tri choisi (nom, date), deux feuilles du même classeur peuvent
               être séparées par un autre document, et un regroupement par
               voisinage afficherait alors le même classeur deux fois.
               Un groupe réduit à UN document est rendu à plat : un dossier
               d'un seul élément ajoute un clic et n'apprend rien. */
            const renderLignes = (liste) => {
              const vus = new Set();
              const sorties = [];
              liste.forEach((v) => {
                const gid = v.import_group_id;
                if (!gid) { sorties.push(renderVideoRow(v)); return; }
                if (vus.has(gid)) return;
                vus.add(gid);
                const lot = liste.filter((x) => x.import_group_id === gid);
                if (lot.length === 1) { sorties.push(renderVideoRow(lot[0])); return; }
                const ouvert = classeursOuverts.has(gid);
                sorties.push(
                  <div key={`grp-${gid}`} data-testid={`import-group-${gid}`}>
                    <button
                      type="button"
                      onClick={() => basculerClasseur(gid)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 bg-emerald-50/70 hover:bg-emerald-100/70 transition-colors text-left"
                      data-testid={`import-group-toggle-${gid}`}
                      title={tr(
                        "Documents issus d'un même classeur Excel (une feuille = un document)",
                        "Documents from a single Excel workbook (one sheet = one document)",
                      )}
                    >
                      {ouvert
                        ? <CaretDown size={14} className="text-emerald-700 flex-shrink-0" />
                        : <CaretRight size={14} className="text-emerald-700 flex-shrink-0" />}
                      <Folder size={16} weight="duotone" className="text-emerald-700 flex-shrink-0" />
                      <span className="text-sm font-semibold text-zinc-800 truncate min-w-0">
                        {lot[0].import_group_name || tr("Classeur", "Workbook")}
                      </span>
                      <span className="text-[11px] text-emerald-800 font-semibold whitespace-nowrap flex-shrink-0">
                        {tr(`${lot.length} feuille(s)`, `${lot.length} sheet(s)`)}
                      </span>
                    </button>
                    {ouvert && (
                      <div className="divide-y divide-border border-l-2 border-emerald-200 ml-4">
                        {lot.map(renderVideoRow)}
                      </div>
                    )}
                  </div>
                );
              });
              return sorties;
            };


            const renderNode = (node, depth = 0) => {
              const isExpanded = !collapsedPaths.has(node.fullPath); // Vincent B704 : défaut ouvert (hors Set)
              const total = countAll(node);
              const hasChildren = node.children.size > 0;
              const isRenamingThis = renamingFolder === node.fullPath;
              return (
                <div
                  key={node.fullPath || node.name}
                  className="bg-card border border-border rounded-xl overflow-hidden"
                  style={{ marginLeft: depth > 0 ? `${Math.min(depth * 16, 48)}px` : undefined }}
                  data-testid={`folder-${node.fullPath.replace(/\s+\/\s+/g, '__').replace(/\s+/g, '_') || 'sans-module'}`}
                >
                  <div className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors">
                    {/* Triangle expand/collapse — cliquable même en mode renommage. */}
                    <button
                      type="button"
                      onClick={(e) => toggleModule(node.fullPath, e)}
                      className="flex items-center gap-3 flex-1 text-left"
                      data-testid={`folder-toggle-${node.fullPath.replace(/\s+\/\s+/g, '__').replace(/\s+/g, '_') || 'sans-module'}`}
                    >
                      {isExpanded ? (
                        <CaretDown size={14} weight="bold" className="text-muted-foreground flex-shrink-0" />
                      ) : (
                        <CaretRight size={14} weight="bold" className="text-muted-foreground flex-shrink-0" />
                      )}
                      {isExpanded ? (
                        <FolderOpen size={20} weight="duotone" className="text-[#002FA7]" />
                      ) : (
                        <Folder size={20} weight="duotone" className="text-muted-foreground" />
                      )}
                      {isRenamingThis ? (
                        <input
                          type="text"
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") { e.preventDefault(); commitFolderRename(node); }
                            else if (e.key === "Escape") { e.preventDefault(); cancelFolderRename(); }
                          }}
                          autoFocus
                          className="font-semibold flex-1 px-2 py-1 border border-[#002FA7] rounded text-foreground bg-background"
                          data-testid={`folder-rename-input-${node.fullPath.replace(/\s+\/\s+/g, '__').replace(/\s+/g, '_') || 'sans-module'}`}
                        />
                      ) : (
                        <span className="font-semibold flex-1 text-foreground truncate">
                          {node.name === "Sans module" ? tr("Sans module", "No module") : node.name}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full flex-shrink-0">
                        {hasChildren
                          ? tr(
                              `${node.children.size} sous-dossier${node.children.size > 1 ? "s" : ""} · `,
                              `${node.children.size} subfolder${node.children.size > 1 ? "s" : ""} · `,
                            )
                          : ""}
                        {total} {tr(`vidéo${total !== 1 ? "s" : ""}`, `video${total !== 1 ? "s" : ""}`)}
                      </span>
                    </button>
                    {/* Vincent B835 (Eric 2026-02-14 SPEC STRICK PROD) —
                        Icône crayon pour renommer le dossier (concepteurs
                        uniquement). Le renommage cascade sur toutes les
                        vidéos incluses (module_name préfixé). */}
                    {isAdmin && node.fullPath && node.name !== "Sans module" && (
                      isRenamingThis ? (
                        <>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); commitFolderRename(node); }}
                            disabled={renamingBusy}
                            className="p-2 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors flex-shrink-0 text-emerald-600 disabled:opacity-40"
                            title="Confirmer"
                            data-testid={`folder-rename-confirm-${node.fullPath.replace(/\s+\/\s+/g, '__').replace(/\s+/g, '_') || 'sans-module'}`}
                          >
                            <Check size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); cancelFolderRename(); }}
                            disabled={renamingBusy}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0 text-red-600 disabled:opacity-40"
                            title={tr("Annuler", "Cancel")}
                            data-testid={`folder-rename-cancel-${node.fullPath.replace(/\s+\/\s+/g, '__').replace(/\s+/g, '_') || 'sans-module'}`}
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); startFolderRename(node); }}
                          className="p-2 hover:bg-secondary rounded-lg transition-colors flex-shrink-0 text-muted-foreground hover:text-[#002FA7]"
                          title={tr("Renommer ce dossier (cascade sur toutes les vidéos incluses)", "Rename this folder (cascades to every video inside)")}
                          data-testid={`folder-rename-btn-${node.fullPath.replace(/\s+\/\s+/g, '__').replace(/\s+/g, '_') || 'sans-module'}`}
                        >
                          <PencilSimple size={16} />
                        </button>
                      )
                    )}
                  </div>
                  {isExpanded && (
                    <div className="border-t border-border">
                      {/* Vincent 2026-05-06 (bug #39) — trier aussi les sous-catégories selon sortBy */}
                      {[...node.children.values()].sort((a, b) => {
                        if (sortBy === "name_desc") return (b.name || "").localeCompare(a.name || "", "fr", { sensitivity: "base" });
                        // name_asc par défaut pour les dossiers (date n/a au niveau catégorie)
                        return (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" });
                      }).map((child) => (
                        <div key={child.fullPath} className="px-2 py-1">
                          {renderNode(child, depth + 1)}
                        </div>
                      ))}
                      {node.videos.length > 0 && (
                        <div className="divide-y divide-border">
                          {renderLignes(node.videos)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            };

            return [...tree.children.values()].sort((a, b) => {
              // Vincent 2026-05-06 (bug #39) — trier les catégories racine aussi
              if (sortBy === "name_desc") return (b.name || "").localeCompare(a.name || "", "fr", { sensitivity: "base" });
              return (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" });
            }).map((child) => renderNode(child, 0))
              .concat(tree.videos.length > 0 ? [
                <div key="__root_videos__" className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-testid="folder-root-videos">
                  {renderLignes(tree.videos)}
                </div>
              ] : []);
          })()}
        </div>
      )}
      </>
      )}

      {/* Vincent #163 — Modal "Assigner à un parcours" */}
      {showAssignModal && (
        <AssignToPathwayModal
          videoIds={selectedVideoIds}
          onClose={() => setShowAssignModal(false)}
          onDone={() => { setShowAssignModal(false); clearSelection(); }}
        />
      )}

      {/* Vincent #275/#291 — Modale d'effacement avec liste à cocher de conservation */}
      {deleteTarget && (
        <DeleteVideoCascadeModal
          video={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(vid) => setVideos((prev) => prev.filter((x) => x.id !== vid))}
        />
      )}

      {/* Vincent B931 (Eric 2026-02-14 SPEC STRICK PROD) — Modal Batch Retag :
          applique 1..N tags canoniques (vocabulaire org ou nouveaux) sur
          tous les documents/vidéos sélectionnés. Mode additif par défaut,
          toggle « Remplacer » pour écraser (avec confirmation). */}
      <BatchTagsModal
        open={showBatchTagsModal}
        onOpenChange={setShowBatchTagsModal}
        selectedIds={selectedVideoIds}
        videosById={videosById}
        onApplied={async () => { await loadAll(); clearSelection(); }}
      />
    </div>
  );
}

// Vincent #163 (Eric 2026-05-24) — Modal d'assignation des vidéos sélectionnées
// à un parcours d'intégration ou d'apprentissage (création ou ajout).
function AssignToPathwayModal({ videoIds, onClose, onDone }) {
  const { tr } = useLang();
  const [pathwayType, setPathwayType] = useState("onboarding");
  const [pathways, setPathways] = useState([]);
  const [selectedPathwayId, setSelectedPathwayId] = useState("__new__");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/pathways?pathway_type=${pathwayType}`, { withCredentials: true });
        setPathways(r.data.pathways || []);
      } catch (_) { /* ignore */ }
    })();
  }, [pathwayType]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { pathway_type: pathwayType, video_ids: videoIds };
      if (selectedPathwayId === "__new__") {
        if (!newName.trim()) { toast.error(tr("Nom du parcours requis", "Pathway name required")); setBusy(false); return; }
        payload.name = newName.trim();
        payload.target_groups = pathwayType === "onboarding" ? ["nouveau"] : [];
      } else {
        payload.pathway_id = selectedPathwayId;
      }
      const r = await axios.post(`${API}/api/pathways/assign-videos`, payload, { withCredentials: true });
      toast.success(r.data.created
        ? `Parcours créé avec ${videoIds.length} vidéo(s)`
        : `${videoIds.length} vidéo(s) ajoutée(s) au parcours`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Échec", "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-pathway-overlay">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-lg mb-4">Assigner {videoIds.length} vidéo(s) à un parcours</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1">{tr("Type de parcours", "Pathway type")}</label>
            <div className="flex gap-2">
              <button
                onClick={() => { setPathwayType("onboarding"); setSelectedPathwayId("__new__"); }}
                className={`flex-1 px-3 py-2 rounded text-sm font-medium ${pathwayType === "onboarding" ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-700"}`}
                data-testid="assign-type-onboarding"
              >{tr("Intégration", "Onboarding")}</button>
              <button
                onClick={() => { setPathwayType("learning"); setSelectedPathwayId("__new__"); }}
                className={`flex-1 px-3 py-2 rounded text-sm font-medium ${pathwayType === "learning" ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-700"}`}
                data-testid="assign-type-learning"
              >Apprentissage</button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">{tr("Parcours destination", "Target pathway")}</label>
            <select
              value={selectedPathwayId}
              onChange={(e) => setSelectedPathwayId(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
              data-testid="assign-pathway-select"
            >
              <option value="__new__">➕ {tr("Nouveau parcours…", "New pathway…")}</option>
              {pathways.map(p => (
                <option key={p.id} value={p.id}>{`${p.name} (${p.video_ids?.length || 0} vidéos)`}</option>
              ))}
            </select>
          </div>

          {selectedPathwayId === "__new__" && (
            <div>
              <label className="block text-xs font-semibold mb-1">{tr("Nom du nouveau parcours *", "New pathway name *")}</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
                placeholder="Ex : Onboarding semaine 1"
                data-testid="assign-new-name"
              />
            </div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm bg-zinc-100 text-zinc-700 rounded hover:bg-zinc-200" data-testid="assign-cancel">{tr("Annuler", "Cancel")}</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm bg-[#002FA7] text-white rounded font-semibold disabled:opacity-50" data-testid="assign-submit">
            {busy ? "Envoi…" : "Assigner"}
          </button>
        </div>
      </div>
    </div>
  );
}
